import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { Role as UserRole } from "../constants/roles";
import { useApp } from "../context/AppContext";
import { pickScopingPersistPayload } from "../utils/pickScopingPersistPayload";
import { buildGapQuestionnaireScopeFromProject } from "../utils/gapQuestionnaireDocument";
import { standardsLabel } from "../utils/contractParties";
import {
  inferLeadSubStepFromProfile,
  normalizeLeadSubStep,
  mergeProjectOnboardingWizard,
  getOnboardingReviewStatusForUi,
} from "../utils/projectOnboardingWizard";
import {
  renamePlitsCatalogueTemplateBasename,
  normalizeArtefactIndexRows,
  catalogueArtefactNameFromBlobPath,
} from "../utils/artefactTemplateCompanyNaming";
import { artefactFileNameUiDisplay, stripTrailingDocumentExtension } from "../utils/artefactDisplayFileName";
import {
  isoToDatetimeLocalValue,
  datetimeLocalValueToIso,
  buildGoogleCalendarTemplateUrl,
} from "../utils/kickoffMeeting";
import { apiTokenRequest, apiConfig } from "../authConfig";
import { message, Modal, Input, Tag, Radio, Space, Spin, Alert, Empty, Tooltip } from "antd";

import { docClassCsv } from "./csvMocks";
import ImpersonationDialog from "./ImpersonationDialog";
import { getActiveImpersonation } from "../utils/impersonationFetch";
import { OnboardingSampleDocxPreviewModal } from "./OnboardingSampleDocxPreviewModal";
import { GapQuestionnaireContractsPanel } from "./GapQuestionnaireContractsPanel";
import SubStepNav from "./page/SubStepNav";
import StageHeader from "./page/StageHeader";
import { GapAnalysisDegradedStandardsBanner } from "./GapAnalysisDegradedStandardsBanner";
import {
  SEVERITY_CATEGORIES,
  SEVERITY_COLUMNS,
  severityCellValue,
  getSignificanceThreshold,
  buildRiskOnboardingPayload,
  riskOnboardingSemanticKey,
  normalizeRiskScale,
} from "../constants/riskAnalysisMatrix";
import { ONBOARDING_SAMPLE_ARTEFACT_ROWS } from "../constants/onboardingSampleArtefacts";
import { formatDdMmYyyy } from "../utils/formatDdMmYyyy";

const AI_GAP_TARGET = 50;

const REGULATORY_ROLE_OPTIONS = [
  { value: "controller", label: "Controller" },
  { value: "processor", label: "Processor" },
  { value: "both", label: "Both" },
];

/** Stable row id derived from full blob virtual path (FNV-1a-ish). */
function artefactRowIdForBlobStoragePath(blobPath) {
  const s = String(blobPath || "");
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `art-blob-${(h >>> 0).toString(16)}`;
}

/** One row per blob under the artefact-templates prefix (no ISO / annex mapping). */
function buildArtefactRowsFromBlobCatalog(blobFiles, defaultClassification, companyName) {
  if (!Array.isArray(blobFiles) || blobFiles.length === 0) return [];
  const sorted = [...blobFiles].filter((f) => f && f.name).sort((a, b) =>
    String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }),
  );
  const rows = [];
  for (const f of sorted) {
    const baseName = (f.name || "").split("/").pop() || "";
    const renamedBase = baseName ? renamePlitsCatalogueTemplateBasename(companyName, baseName) : "";
    const displayBase = renamedBase || baseName;
    const artefactName =
      catalogueArtefactNameFromBlobPath(baseName) ||
      stripTrailingDocumentExtension(baseName) ||
      baseName;
    rows.push({
      id: artefactRowIdForBlobStoragePath(f.name),
      name: artefactName,
      templateId: displayBase || baseName,
      classification: defaultClassification || "",
      blobUrl: f.url || undefined,
      fileName: displayBase || undefined,
    });
  }
  return rows;
}

const ADVISOR_SUBSTEP_KEYS = ["classification", "contracts"];

function normalizeAdvisorSubStep(value) {
  const s = String(value || "").trim();
  if (s === "samples") return "classification";
  return ADVISOR_SUBSTEP_KEYS.includes(s) ? s : null;
}

function hasImportedArtefactTemplate(row) {
  return !!(row && (row.blobUrl || row.fileName));
}

function severityCellPillClass(cell) {
  if (cell === "NA") return "";
  const c = String(cell);
  if (c === "1") return "px-2 py-0.5 bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-hero)] rounded font-bold text-[10px]";
  if (c === "2") return "px-2 py-0.5 bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] rounded font-bold text-[10px]";
  if (c === "3") return "px-2 py-0.5 bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] rounded font-bold text-[10px]";
  if (c === "4") return "px-2 py-0.5 bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] rounded font-bold text-[10px]";
  if (c === "5") return "px-2 py-0.5 bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] rounded font-bold text-[10px]";
  if (c === "6" || c === "7") return "px-2 py-0.5 bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] rounded font-bold text-[10px]";
  if (c.includes("8")) return "px-2 py-0.5 bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] rounded font-bold text-[10px]";
  if (c === "10") return "px-2 py-0.5 bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] rounded font-bold text-[10px]";
  return "px-2 py-0.5 bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-hero)] rounded font-bold text-[10px]";
}
function normalizeStoredGapQuestion(q) {
  if (!q || typeof q !== "object") return null;
  const qNo = q.qNo || q.questionNo || "";
  const id =
    q.id != null && String(q.id).trim() !== ""
      ? String(q.id)
      : qNo
        ? `q-${String(qNo).replace(/\s+/g, "")}`
        : null;
  if (!id) return null;
  return {
    id,
    qNo,
    question: q.question || "",
    domain: q.domain || "General",
    responseType: q.responseType || "",
    selected: q.selected !== false,
    source: q.source === "ai" || q.source === "custom" || q.source === "bank" ? q.source : "bank",
  };
}
function initialGapQuestionsFromProfile(project) {
  const raw = project?.profile?.gapAnalysisQuestionnaire;
  if (!raw?.questions || !Array.isArray(raw.questions)) return [];
  return raw.questions.map(normalizeStoredGapQuestion).filter(Boolean);
}
function buildGapAnalysisPersistPayload(questions) {
  const aiOnly = questions.filter((q) => q.source === "ai");
  const distinctAi = new Set(aiOnly.map((q) => q.domain).filter(Boolean));
  const allDomains = new Set(questions.map((q) => q.domain).filter(Boolean));
  return {
    version: 1,
    questions: questions.map((q) => ({
      id: q.id,
      qNo: q.qNo,
      question: q.question,
      domain: q.domain,
      responseType: q.responseType,
      selected: q.selected !== false,
      source: q.source || "bank",
    })),
    distinctDomainsInAiBatch: distinctAi.size,
    aiDomainLabels: [...distinctAi].sort(),
    distinctDomainsInSelection: allDomains.size,
    updatedAt: Date.now(),
  };
}
/** Stable compare for skipping redundant saves (e.g. after reload / profile re-hydrate). Ignores updatedAt. */
function gapQuestionnaireSemanticKey(g) {
  if (!g || typeof g !== "object") return "";
  const questions = Array.isArray(g.questions) ? g.questions : [];
  const labels = Array.isArray(g.aiDomainLabels) ? [...g.aiDomainLabels].map(String).sort() : [];
  return JSON.stringify({
    version: g.version ?? 1,
    questions,
    distinctDomainsInAiBatch: g.distinctDomainsInAiBatch ?? 0,
    aiDomainLabels: labels,
    distinctDomainsInSelection: g.distinctDomainsInSelection ?? 0,
  });
}
const ISSUE_CATEGORIES = ["Internal Issues", "External Issues", "Risk and Threats", "Opportunities for Improvement"];
function emptyIssueSelections() {
  return Object.fromEntries(ISSUE_CATEGORIES.map((c) => [c, []]));
}
function normalizeLegacyIssueEntry(entry) {
  if (typeof entry === "string" && entry.trim()) return { bankItemId: null, issue: entry.trim() };
  if (!entry || typeof entry !== "object") return null;
  const issue = typeof entry.issue === "string" ? entry.issue.trim() : "";
  if (!issue) return null;
  return {
    bankItemId: entry.bankItemId != null && entry.bankItemId !== "" ? String(entry.bankItemId) : null,
    issue,
  };
}
function buildIssuesThreatsPersistPayload(byCategory) {
  const byCategoryOut = {};
  for (const cat of ISSUE_CATEGORIES) {
    const rows = byCategory[cat] || [];
    byCategoryOut[cat] = rows.map((r) => ({
      bankItemId: r.bankItemId || null,
      issue: r.issue || "",
    }));
  }
  return {
    version: 1,
    byCategory: byCategoryOut,
    updatedAt: Date.now(),
  };
}
function issuesThreatsSemanticKey(payload) {
  if (!payload || typeof payload !== "object") return "";
  const bc = payload.byCategory;
  if (!bc || typeof bc !== "object") return "";
  const keys = ISSUE_CATEGORIES.map((c) => {
    const rows = Array.isArray(bc[c]) ? bc[c] : [];
    return [c, rows.map((r) => ({ i: r.bankItemId || null, t: r.issue || "" }))];
  });
  return JSON.stringify(Object.fromEntries(keys));
}
function parseCsv(text) {
  const rows = [];
  let current = "";
  let inQuotes = false;
  let row = [];
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') {
        current += '"';
        i++;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        current += c;
      }
    } else {
      if (c === '"') {
        inQuotes = true;
      } else if (c === ",") {
        row.push(current.trim());
        current = "";
      } else if (c === "\n" || c === "\r" && text[i + 1] === "\n") {
        row.push(current.trim());
        current = "";
        if (row.some((cell) => cell !== "")) rows.push(row);
        row = [];
        if (c === "\r") i++;
      } else {
        current += c;
      }
    }
  }
  row.push(current.trim());
  if (row.some((cell) => cell !== "")) rows.push(row);
  return rows;
}

const ONBOARDING_STATUS_STEPS = [
  { key: "draft", title: "Draft", sub: "Risk identification", icon: "file" },
  { key: "review", title: "Under Review", sub: "SPOC reviewing", icon: "search" },
  { key: "approved", title: "Approved", sub: "Risks approved", icon: "check" },
  { key: "published", title: "Published", sub: "Live dashboard", icon: "pencil" },
];

function onboardingStatusStepIndex(status) {
  if (status === "Under Review") return 1;
  if (status === "Approved by SPOC") return 2;
  if (status === "Finalized") return 3;
  return 0;
}

function onboardingStatusStepIcon(kind) {
  const common = { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" };
  if (kind === "file") {
    return /* @__PURE__ */ jsx("svg", { ...common, children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" }) });
  }
  if (kind === "search") {
    return /* @__PURE__ */ jsx("svg", { ...common, children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" }) });
  }
  if (kind === "check") {
    return /* @__PURE__ */ jsx("svg", { ...common, children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" }) });
  }
  return /* @__PURE__ */ jsx("svg", { ...common, children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" }) });
}

/** Shared with workspace SPOC contract view so onboarding progress matches LD/advisor. */
export function renderOnboardingStatusBar(step3Status) {
  const currentIdx = onboardingStatusStepIndex(step3Status);
  const row = [];
  ONBOARDING_STATUS_STEPS.forEach((step, i) => {
    const reached = i <= currentIdx;
    row.push(
      /* @__PURE__ */ jsxs(
        "div",
        {
          key: step.key,
          className: "flex flex-1 flex-col items-center text-center min-w-0 px-0.5",
          children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                className: `flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-md ${reached ? "bg-[color:var(--color-step-reached)] text-white" : "border-2 border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)]"}`,
                children: onboardingStatusStepIcon(step.icon),
              },
            ),
            /* @__PURE__ */ jsx("div", {
              className: `mt-2.5 text-[8px] font-black uppercase tracking-widest text-balance sm:text-[10px] ${reached ? "text-[color:var(--color-accent)]" : "text-[color:var(--color-text-hero)]"}`,
              children: step.title,
            }),
            /* @__PURE__ */ jsx("div", {
              className: "mt-0.5 text-[8px] font-medium leading-snug text-[color:var(--color-text-muted)] sm:text-[9px]",
              children: step.sub,
            }),
          ],
        },
      ),
    );
    if (i < ONBOARDING_STATUS_STEPS.length - 1) {
      row.push(
        /* @__PURE__ */ jsx("div", {
          key: `${step.key}-connector`,
          className: `mx-1 h-1 min-w-[12px] max-w-[4rem] flex-1 self-center rounded-full sm:mx-2 ${i < currentIdx ? "bg-[color:var(--color-accent-fill)]" : "bg-[color:var(--color-bg-alt)]"}`,
          "aria-hidden": true,
        }),
      );
    }
  });
  return /* @__PURE__ */ jsx("div", {
    className: "rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-3 py-5 shadow-lg sm:px-6 md:px-8",
    children: /* @__PURE__ */ jsx("div", {
      className: "mx-auto flex w-full max-w-5xl items-center",
      children: row,
    }),
  });
}

export const ProjectOnboarding = ({ project, user }) => {
  const {
    updateProjectProfile,
    saveScopingProfile,
    fetchProjectProfile,
    fetchProjectMembers,
    createMeeting,
    fetchNotifications,
    updateRegulatoryRoleAPI,
    confirmRegulatoryRoleAPI,
  } = useApp();
  const isLeadAdvisor = user?.role === UserRole.LEAD_ADVISOR || user?.role === UserRole.ADMIN;
  const isAdvisor = user?.role === UserRole.ADVISOR;
  const isSPOC = user?.role === UserRole.COMPANY_SPOC;
  // Task #128 fast-follow — regulatory role (controller/processor/both) persona gate.
  // Mirrors the API's ABAC exactly (CLAUDE.md §6): LA/Advisor (or platform_admin, folded
  // into isLeadAdvisor already) draft; LA/SPOC confirm. Union, not two LA code paths.
  // Company Delegate matches neither set, so the control renders nothing for that persona.
  const canDraftRegulatoryRole = isLeadAdvisor || isAdvisor;
  const canConfirmRegulatoryRole = isLeadAdvisor || isSPOC;

  /**
   * "Act on behalf of Advisor" — backlog #13 (Artefact Index / artefactIndexRows).
   * .claude/design/approval-matrix-clarification.md (R015, "Read B"): the Advisor-only gate
   * on this screen is NOT weakened — a Lead Advisor can instead start an audited impersonation
   * session and complete the Advisor's step through it. Session-polling mirrors
   * LeadAdvisorApprovalMatrixPanel.jsx's identical pattern verbatim: sessionStorage writes
   * don't trigger a re-render on their own, and a session can expire while this screen is open.
   * Backend contract: assertProjectOnboardingWizardSaveAllowed (registerRouteHelpers.ts)
   * accepts an `artefactIndexRows` write from an impersonated `advisor` session.
   */
  const [activeImpersonationSession, setActiveImpersonationSession] = useState(() => getActiveImpersonation());
  useEffect(() => {
    const tick = () => setActiveImpersonationSession(getActiveImpersonation());
    tick();
    const timer = setInterval(tick, 15000);
    return () => clearInterval(timer);
  }, []);
  const advisorImpersonationSessionMatches =
    isLeadAdvisor &&
    !!activeImpersonationSession &&
    activeImpersonationSession.effectiveRole === "advisor" &&
    activeImpersonationSession.projectId === project?.id;
  /**
   * Engineering Director REQUEST-CHANGES (backlog #13 follow-up): the backend's
   * identity-verification gate (assertProjectOnboardingWizardSaveAllowed,
   * registerRouteHelpers.ts) rejects a role-generic advisor session (no `effectiveUserId`) for
   * `artefactIndexRows` — so a session missing it must never reach the editing panel at all.
   * Gating only the Save click let an LA do real data entry before finding out at Save time.
   */
  const advisorImpersonationActive =
    advisorImpersonationSessionMatches && !!activeImpersonationSession?.effectiveUserId;
  const advisorImpersonationNeedsNamedUser =
    advisorImpersonationSessionMatches && !activeImpersonationSession?.effectiveUserId;
  const [advisorActOnBehalfOpen, setAdvisorActOnBehalfOpen] = useState(false);
  /**
   * The backend's identity-verification gate on `artefactIndexRows`
   * (assertProjectOnboardingWizardSaveAllowed, registerRouteHelpers.ts) rejects a
   * role-generic advisor-impersonation session (no `effectiveUserId`) — the LA must name a
   * specific Advisor project member when starting the session. Fetched only for the LD, so the
   * ImpersonationDialog's "specific user" picker has real Advisor rows to offer.
   */
  const [advisorMembersForActOnBehalf, setAdvisorMembersForActOnBehalf] = useState([]);
  useEffect(() => {
    if (!isLeadAdvisor || !project?.id || !fetchProjectMembers) return;
    let cancelled = false;
    void (async () => {
      const members = await fetchProjectMembers(project.id);
      if (cancelled) return;
      setAdvisorMembersForActOnBehalf((members || []).filter((m) => m.role === "advisor"));
    })();
    return () => {
      cancelled = true;
    };
  }, [isLeadAdvisor, project?.id, fetchProjectMembers]);

  const { instance: msalInstance } = useMsal();
  const msalAccount = msalInstance.getActiveAccount();
  const instanceRef = useRef(msalInstance);
  const accountRef = useRef(msalAccount);
  useEffect(() => {
    instanceRef.current = msalInstance;
  }, [msalInstance]);
  useEffect(() => {
    accountRef.current = msalAccount;
  }, [msalAccount]);
  const getApiToken = useCallback(async () => {
    const tokenResponse = await instanceRef.current.acquireTokenSilent({
      ...apiTokenRequest,
      account: accountRef.current,
    });
    return tokenResponse.accessToken;
  }, []);
  const [leadSubStep, setLeadSubStep] = useState(() =>
    project?.id && project?.profile ? inferLeadSubStepFromProfile(project.profile) : "questions",
  );
  const leadSubStepHydrationKey = useMemo(() => {
    const w = project?.profile?.projectOnboardingWizard;
    const g = project?.profile?.gapQuestionnaireDocument?.finalized ? 1 : 0;
    const ex = w?.activeLeadSubStep ?? "";
    const ast = w?.advisorArtefactsStatus ?? "";
    const kt = Array.isArray(w?.kickoffTranscripts) ? w.kickoffTranscripts.length : 0;
    return `${project?.id || ""}|${ex}|${w?.selectQuestions ? 1 : 0}|${w?.riskScaleComplete ? 1 : 0}|${g}|${ast}|${w?.kickoffScheduled ? 1 : 0}|${kt}|${w?.kickoffStartAt || ""}`;
  }, [
    project?.id,
    project?.profile?.projectOnboardingWizard,
    project?.profile?.gapQuestionnaireDocument?.finalized,
  ]);
  useEffect(() => {
    if (!project?.id) return;
    setLeadSubStep(inferLeadSubStepFromProfile(project.profile));
  }, [leadSubStepHydrationKey, project?.id]);
  const [advisorSubStep, setAdvisorSubStep] = useState(() => {
    const s = normalizeAdvisorSubStep(project?.profile?.projectOnboardingWizard?.activeAdvisorSubStep);
    return s || "classification";
  });
  const advisorSubStepHydrationKey = useMemo(
    () => `${project?.id || ""}|${project?.profile?.projectOnboardingWizard?.activeAdvisorSubStep ?? ""}`,
    [project?.id, project?.profile?.projectOnboardingWizard?.activeAdvisorSubStep],
  );
  useEffect(() => {
    if (!project?.id) return;
    const next = normalizeAdvisorSubStep(project?.profile?.projectOnboardingWizard?.activeAdvisorSubStep) || "classification";
    setAdvisorSubStep(next);
  }, [advisorSubStepHydrationKey, project?.id]);
  const [step3Status, setStep3Status] = useState(
    () => project?.profile?.projectOnboardingWizard?.onboardingReviewStatus || "Draft",
  );
  // Task #128 fast-follow: regulatory role draft/confirm control (DPDP/GDPR controller-processor-both).
  // Source of truth is project.regulatoryRole* (hydrated by fetchProjectProfile, see AppContext.jsx) —
  // local state here is only the pending Select value before "Save Draft" is clicked.
  const [regulatoryRoleDraft, setRegulatoryRoleDraft] = useState(() => project?.regulatoryRole || "");
  const [regulatoryRoleSaving, setRegulatoryRoleSaving] = useState(false);
  const [regulatoryRoleConfirming, setRegulatoryRoleConfirming] = useState(false);
  useEffect(() => {
    setRegulatoryRoleDraft(project?.regulatoryRole || "");
  }, [project?.id, project?.regulatoryRole]);
  const [isOnboardingReviewOpen, setIsOnboardingReviewOpen] = useState(false);
  const [onboardingReviewTarget, setOnboardingReviewTarget] = useState(null);
  const [onboardingReviewComment, setOnboardingReviewComment] = useState("");
  const [onboardingReviewSubmitting, setOnboardingReviewSubmitting] = useState(false);
  /** Lead Artefacts Index: educator sample PLITS docs — opens mammoth modal (not artefact blobs). */
  const [leadOnboardingSamplePreview, setLeadOnboardingSamplePreview] = useState(null);
  const [advisorOnboardingSamplePreview, setAdvisorOnboardingSamplePreview] = useState(null);
  const onboardingReviewHydrateKey = useMemo(
    () => `${project?.id ?? ""}|${project?.profile?.projectOnboardingWizard?.onboardingReviewStatus ?? ""}`,
    [project?.id, project?.profile?.projectOnboardingWizard?.onboardingReviewStatus],
  );
  const [bankQuestions, setBankQuestions] = useState([]);
  const [totalFromBank, setTotalFromBank] = useState(null);
  const [bankMetaDomains, setBankMetaDomains] = useState([]);
  const [bankLoading, setBankLoading] = useState(true);
  const [bankError, setBankError] = useState(null);
  const [qbVersions, setQbVersions] = useState([]);
  const [qbVersionsLoading, setQbVersionsLoading] = useState(true);
  const [qbVersionsError, setQbVersionsError] = useState(null);
  const [selectedQbVersionId, setSelectedQbVersionId] = useState(null);
  const [aiSelecting, setAiSelecting] = useState(false);
  const [aiError, setAiError] = useState(null);
  // `degradedStandards[]` from the ai-select-questions response — populated
  // whenever the project's own selected standard (GDPR / ISO 27701 / ISO
  // 42001) has no seeded question-bank content yet and the served pool
  // silently fell back to ISO 27001 baseline only. Visibility-only: never
  // gates the picker or the submit flow. See GapAnalysisDegradedStandardsBanner.
  const [degradedStandards, setDegradedStandards] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState(() => initialGapQuestionsFromProfile(project));
  const [gapSaveState, setGapSaveState] = useState("idle");
  const [questionsStepSubmitting, setQuestionsStepSubmitting] = useState(false);
  const [riskScaleStepSubmitting, setRiskScaleStepSubmitting] = useState(false);
  const profileRef = useRef(project?.profile || {});
  const allowGapPersistRef = useRef(false);
  /** True after user/AI changes questions; false after hydrate from server. Prevents debounced save from wiping DB with [] before state catches up. */
  const gapQuestionsUserEditedRef = useRef(false);
  useEffect(() => {
    profileRef.current = project?.profile || {};
  }, [project?.profile]);
  const persistActiveLeadSubStep = useCallback(
    async (key) => {
      const step = normalizeLeadSubStep(key);
      if (!step || !project?.id || !saveScopingProfile || !updateProjectProfile) return;
      const base = profileRef.current || {};
      const newProfile = mergeProjectOnboardingWizard(base, { activeLeadSubStep: step });
      profileRef.current = newProfile;
      updateProjectProfile(project.id, newProfile);
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (!ok) console.warn("Project onboarding: active tab may not have persisted.");
      else if (fetchProjectProfile) await fetchProjectProfile(project.id);
    },
    [project?.id, saveScopingProfile, updateProjectProfile, fetchProjectProfile],
  );
  const persistActiveAdvisorSubStep = useCallback(
    async (key) => {
      const step = normalizeAdvisorSubStep(key);
      if (!step || !project?.id || !saveScopingProfile || !updateProjectProfile) return;
      const base = profileRef.current || {};
      const newProfile = mergeProjectOnboardingWizard(base, { activeAdvisorSubStep: step });
      profileRef.current = newProfile;
      updateProjectProfile(project.id, newProfile);
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (!ok) console.warn("Project onboarding: advisor tab may not have persisted.");
      else if (fetchProjectProfile) await fetchProjectProfile(project.id);
    },
    [project?.id, saveScopingProfile, updateProjectProfile, fetchProjectProfile],
  );
  useEffect(() => {
    const s = project?.profile?.projectOnboardingWizard?.onboardingReviewStatus || "Draft";
    setStep3Status(s);
  }, [onboardingReviewHydrateKey]);
  const step3StatusForUi = useMemo(() => {
    if (isSPOC) return getOnboardingReviewStatusForUi(project?.profile || {});
    return step3Status;
  }, [isSPOC, project?.profile, step3Status]);
  const persistOnboardingReviewStatus = useCallback(
    async (next, reviewComment = "") => {
      if (!project?.id || !saveScopingProfile || !updateProjectProfile) {
        setStep3Status(next);
        return;
      }
      setStep3Status(next);
      const base = profileRef.current || project.profile || {};
      const note = String(reviewComment || "").trim();
      const nextComments = note
        ? ((Array.isArray(base.comments) ? base.comments : []).concat([{
            actorRole: user?.role || "UNKNOWN",
            text: note,
            timestamp: Date.now(),
            section: "project-onboarding",
            targetStatus: next,
          }]))
        : base.comments;
      const merged = mergeProjectOnboardingWizard(base, { onboardingReviewStatus: next });
      const newProfile = nextComments === undefined ? merged : { ...merged, comments: nextComments };
      profileRef.current = newProfile;
      updateProjectProfile(project.id, newProfile);
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (!ok) console.warn("Onboarding review status may not have persisted.");
    },
    [project?.id, project?.profile, saveScopingProfile, updateProjectProfile, user?.role],
  );
  // Task #128 fast-follow — draft-save regulatory role (LA/Advisor). Backend un-confirms on
  // any new draft write, so we don't optimistically keep a stale "confirmed" flag around.
  const saveRegulatoryRoleDraft = useCallback(async () => {
    if (!project?.id || !regulatoryRoleDraft || !updateRegulatoryRoleAPI) return;
    setRegulatoryRoleSaving(true);
    try {
      const ok = await updateRegulatoryRoleAPI(project.id, regulatoryRoleDraft);
      if (ok) message.success("Regulatory role draft saved.");
      else message.error("Could not save the regulatory role draft. Try again.");
    } finally {
      setRegulatoryRoleSaving(false);
    }
  }, [project?.id, regulatoryRoleDraft, updateRegulatoryRoleAPI]);

  // Task #128 fast-follow — confirm the current draft (LA/SPOC). Backend 400s if nothing
  // is drafted yet; canConfirmRegulatoryRoleAction below also guards that client-side.
  //
  // Bug fix (sim-spoc finding): HTTP success alone does not mean the confirmation
  // persisted — in an un-migrated environment the backend documents this as a no-op and
  // reports it honestly via `regulatoryRoleConfirmed: false`. Only show the success toast
  // when that flag is actually true; otherwise warn the user explicitly rather than
  // letting them believe the confirmation went through while the badge stays "Draft".
  const confirmRegulatoryRole = useCallback(async () => {
    if (!project?.id || !confirmRegulatoryRoleAPI) return;
    setRegulatoryRoleConfirming(true);
    try {
      const result = await confirmRegulatoryRoleAPI(project.id);
      if (result?.ok && result.regulatoryRoleConfirmed) {
        message.success("Regulatory role confirmed.");
      } else if (result?.ok) {
        message.warning(
          "The confirmation could not be recorded. The regulatory role is still in Draft - contact an administrator.",
        );
      } else {
        message.error("Could not confirm the regulatory role. Try again.");
      }
    } finally {
      setRegulatoryRoleConfirming(false);
    }
  }, [project?.id, confirmRegulatoryRoleAPI]);

  const openOnboardingReviewModal = useCallback((targetStatus) => {
    setOnboardingReviewTarget(targetStatus);
    setOnboardingReviewComment("");
    setIsOnboardingReviewOpen(true);
  }, []);
  const submitOnboardingReviewWithComment = useCallback(async () => {
    if (!onboardingReviewTarget) return;
    setOnboardingReviewSubmitting(true);
    try {
      await persistOnboardingReviewStatus(onboardingReviewTarget, onboardingReviewComment);
      setIsOnboardingReviewOpen(false);
    } finally {
      setOnboardingReviewSubmitting(false);
    }
  }, [onboardingReviewComment, onboardingReviewTarget, persistOnboardingReviewStatus]);
  const gapPersistSnapshot = useMemo(
    () => JSON.stringify(project?.profile?.gapAnalysisQuestionnaire ?? null),
    [project?.profile?.gapAnalysisQuestionnaire],
  );
  useEffect(() => {
    allowGapPersistRef.current = false;
    gapQuestionsUserEditedRef.current = false;
    const raw = project?.profile?.gapAnalysisQuestionnaire;
    if (raw?.questions && Array.isArray(raw.questions)) {
      const next = raw.questions.map(normalizeStoredGapQuestion).filter(Boolean);
      setSelectedQuestions(next);
    } else {
      setSelectedQuestions([]);
    }
    const id = requestAnimationFrame(() => {
      allowGapPersistRef.current = true;
    });
    return () => cancelAnimationFrame(id);
  }, [project?.id, gapPersistSnapshot]);
  useEffect(() => {
    if (!project?.id || !saveScopingProfile || !updateProjectProfile) return;
    if (!allowGapPersistRef.current) return;
    const t = setTimeout(async () => {
      const storedGap = profileRef.current?.gapAnalysisQuestionnaire;
      const storedQuestionCount = Array.isArray(storedGap?.questions) ? storedGap.questions.length : 0;
      if (selectedQuestions.length === 0 && storedQuestionCount > 0 && !gapQuestionsUserEditedRef.current) {
        return;
      }
      const gapAnalysisQuestionnaire = buildGapAnalysisPersistPayload(selectedQuestions);
      if (gapQuestionnaireSemanticKey(gapAnalysisQuestionnaire) === gapQuestionnaireSemanticKey(storedGap)) {
        return;
      }
      setGapSaveState("saving");
      const base = profileRef.current || {};
      const newProfile = { ...base, gapAnalysisQuestionnaire };
      profileRef.current = newProfile;
      updateProjectProfile(project.id, newProfile);
      try {
        const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
        setGapSaveState(ok ? "saved" : "error");
        if (ok) setTimeout(() => setGapSaveState("idle"), 2e3);
      } catch {
        setGapSaveState("error");
      }
    }, 650);
    return () => clearTimeout(t);
  }, [selectedQuestions, project?.id, saveScopingProfile, updateProjectProfile]);
  const [issueBankItems, setIssueBankItems] = useState([]);
  const [issueBankLoading, setIssueBankLoading] = useState(true);
  const [issueBankError, setIssueBankError] = useState(null);
  const [aiIssuesSelecting, setAiIssuesSelecting] = useState(false);
  const [aiIssuesError, setAiIssuesError] = useState(null);
  const [selectedIssueRows, setSelectedIssueRows] = useState(() => emptyIssueSelections());
  const allowIssuesPersistRef = useRef(false);
  const issuesPersistSnapshot = useMemo(
    () => JSON.stringify(project?.profile?.issuesThreatsOpportunities ?? null),
    [project?.profile?.issuesThreatsOpportunities],
  );
  useEffect(() => {
    allowIssuesPersistRef.current = false;
    const raw = project?.profile?.issuesThreatsOpportunities;
    if (raw?.byCategory && typeof raw.byCategory === "object") {
      const next = emptyIssueSelections();
      for (const cat of ISSUE_CATEGORIES) {
        const arr = raw.byCategory[cat];
        next[cat] = Array.isArray(arr) ? arr.map(normalizeLegacyIssueEntry).filter(Boolean) : [];
      }
      setSelectedIssueRows(next);
    } else {
      setSelectedIssueRows(emptyIssueSelections());
    }
    const id = requestAnimationFrame(() => {
      allowIssuesPersistRef.current = true;
    });
    return () => cancelAnimationFrame(id);
  }, [project?.id, issuesPersistSnapshot]);
  useEffect(() => {
    if (!project?.id || !saveScopingProfile || !updateProjectProfile) return;
    if (!allowIssuesPersistRef.current) return;
    const t = setTimeout(async () => {
      const issuesThreatsOpportunities = buildIssuesThreatsPersistPayload(selectedIssueRows);
      const stored = profileRef.current?.issuesThreatsOpportunities;
      if (issuesThreatsSemanticKey(issuesThreatsOpportunities) === issuesThreatsSemanticKey(stored)) {
        return;
      }
      const base = profileRef.current || {};
      const newProfile = { ...base, issuesThreatsOpportunities };
      profileRef.current = newProfile;
      updateProjectProfile(project.id, newProfile);
      try {
        await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      } catch {
        /* non-blocking */
      }
    }, 650);
    return () => clearTimeout(t);
  }, [selectedIssueRows, project?.id, saveScopingProfile, updateProjectProfile]);
  const [riskScale, setRiskScale] = useState("5");
  const allowRiskPersistRef = useRef(false);
  const riskOnboardingSnapshot = useMemo(
    () => JSON.stringify(project?.profile?.riskOnboarding ?? null),
    [project?.profile?.riskOnboarding],
  );
  useEffect(() => {
    allowRiskPersistRef.current = false;
    const ro = project?.profile?.riskOnboarding;
    if (ro?.riskScale) {
      setRiskScale(normalizeRiskScale(ro.riskScale));
    }
    const id = requestAnimationFrame(() => {
      allowRiskPersistRef.current = true;
    });
    return () => cancelAnimationFrame(id);
  }, [project?.id, riskOnboardingSnapshot]);
  useEffect(() => {
    if (!project?.id || !saveScopingProfile || !updateProjectProfile) return;
    if (!allowRiskPersistRef.current) return;
    const t = setTimeout(async () => {
      const riskOnboarding = buildRiskOnboardingPayload(riskScale);
      const stored = profileRef.current?.riskOnboarding;
      if (riskOnboardingSemanticKey(riskOnboarding) === riskOnboardingSemanticKey(stored)) {
        return;
      }
      const base = profileRef.current || {};
      const newProfile = { ...base, riskOnboarding };
      profileRef.current = newProfile;
      updateProjectProfile(project.id, newProfile);
      try {
        await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      } catch {
        /* non-blocking */
      }
    }, 650);
    return () => clearTimeout(t);
  }, [riskScale, project?.id, saveScopingProfile, updateProjectProfile]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIssueBankLoading(true);
        setIssueBankError(null);
        const token = await getApiToken();
        const headers = { Authorization: `Bearer ${token}` };
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/issues-bank`, { headers });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok || !json.success) {
          throw new Error(json.error?.message || "Failed to load issue bank");
        }
        setIssueBankItems(json.data.items || []);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setIssueBankError(e.message || "Failed to load issue bank");
          setIssueBankItems([]);
        }
      } finally {
        if (!cancelled) setIssueBankLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getApiToken, project?.id]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setQbVersionsLoading(true);
        setQbVersionsError(null);
        const token = await getApiToken();
        const headers = { Authorization: `Bearer ${token}` };
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/question-bank/versions`, { headers });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok || !json.success) {
          throw new Error(json.error?.message || "Failed to load question bank versions");
        }
        const list = json.data.versions || [];
        setQbVersions(list);
        setSelectedQbVersionId((prev) => {
          if (prev && list.some((v) => v.id === prev)) return prev;
          return list.length ? list[list.length - 1].id : null;
        });
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setQbVersionsError(e.message || "Failed to load versions");
          setQbVersions([]);
          setSelectedQbVersionId(null);
        }
      } finally {
        if (!cancelled) setQbVersionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getApiToken, project?.id]);
  useEffect(() => {
    if (qbVersionsLoading) return;
    let cancelled = false;
    (async () => {
      if (!selectedQbVersionId) {
        setBankLoading(false);
        setBankError(qbVersionsError);
        setTotalFromBank(0);
        setBankMetaDomains([]);
        setBankQuestions([]);
        return;
      }
      try {
        setBankLoading(true);
        setBankError(null);
        const token = await getApiToken();
        const headers = { Authorization: `Bearer ${token}` };
        const vq = encodeURIComponent(selectedQbVersionId);
        const [metaRes, qRes] = await Promise.all([
          fetch(`${apiConfig.baseUrl}/api/v1/questions/meta?versionId=${vq}`, { headers }),
          fetch(`${apiConfig.baseUrl}/api/v1/questions?versionId=${vq}`, { headers }),
        ]);
        const metaJson = await metaRes.json();
        const qJson = await qRes.json();
        if (cancelled) return;
        if (!metaRes.ok || !metaJson.success) {
          throw new Error(metaJson.error?.message || "Failed to load question bank meta");
        }
        if (!qRes.ok || !qJson.success) {
          throw new Error(qJson.error?.message || "Failed to load question bank");
        }
        setTotalFromBank(metaJson.data.total ?? 0);
        setBankMetaDomains(metaJson.data.domains || []);
        const rows = qJson.data.questions || [];
        setBankQuestions(
          rows.map((q) => ({
            id: q.id,
            qNo: q.questionNo || "",
            question: q.question || "",
            domain: q.domain || "General",
            responseType: q.responseType || "",
            selected: false,
            source: "bank",
          })).filter((q) => q.question),
        );
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setBankError(e.message || "Failed to load questions");
          setTotalFromBank(0);
          setBankQuestions([]);
        }
      } finally {
        if (!cancelled) setBankLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getApiToken, project?.id, selectedQbVersionId, qbVersionsLoading]);
  const runAiGapSelection = async () => {
    if (!project?.id) return;
    setAiError(null);
    setDegradedStandards([]);
    setAiSelecting(true);
    try {
      const token = await getApiToken();
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/gap-analysis/ai-select-questions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: "{}",
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error?.message || `AI selection failed (${res.status})`);
      }
      const list = json.data.questions || [];
      setDegradedStandards(Array.isArray(json.data.degradedStandards) ? json.data.degradedStandards : []);
      gapQuestionsUserEditedRef.current = true;
      setSelectedQuestions(
        list.map((q) => ({
          id: q.id != null ? String(q.id) : `q-${String(q.questionNo || "").replace(/\s+/g, "")}`,
          qNo: q.questionNo || "",
          question: q.question || "",
          domain: q.domain || "General",
          responseType: q.responseType || "",
          selected: true,
          source: "ai",
        })),
      );
    } catch (e) {
      console.error(e);
      setAiError(e.message || "AI selection failed");
    } finally {
      setAiSelecting(false);
    }
  };
  const runAiIssueSelection = async () => {
    if (!project?.id) return;
    setAiIssuesError(null);
    setAiIssuesSelecting(true);
    try {
      const token = await getApiToken();
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/onboarding/ai-select-issues`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: "{}",
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error?.message || `AI issue selection failed (${res.status})`);
      }
      const by = json.data.byCategory || {};
      const next = emptyIssueSelections();
      for (const cat of ISSUE_CATEGORIES) {
        const rows = by[cat] || [];
        next[cat] = rows.map((row) => ({ bankItemId: row.id, issue: row.issue || "" }));
      }
      setSelectedIssueRows(next);
    } catch (e) {
      console.error(e);
      setAiIssuesError(e.message || "AI issue selection failed");
    } finally {
      setAiIssuesSelecting(false);
    }
  };
  const submitQuestionsAndContinueToRiskScale = async () => {
    if (!project?.id || !saveScopingProfile || !updateProjectProfile) return;
    setQuestionsStepSubmitting(true);
    try {
      const base = profileRef.current || {};
      const gapAnalysisQuestionnaire = buildGapAnalysisPersistPayload(selectedQuestions);
      const issuesThreatsOpportunities = buildIssuesThreatsPersistPayload(selectedIssueRows);
      const prevWizard = base.projectOnboardingWizard && typeof base.projectOnboardingWizard === "object" ? base.projectOnboardingWizard : {};
      const projectOnboardingWizard = { ...prevWizard, selectQuestions: true, activeLeadSubStep: "risk-scale" };
      const riskOnboarding = buildRiskOnboardingPayload(riskScale);
      const newProfile = {
        ...base,
        gapAnalysisQuestionnaire,
        issuesThreatsOpportunities,
        projectOnboardingWizard,
        riskOnboarding,
      };
      profileRef.current = newProfile;
      updateProjectProfile(project.id, newProfile);
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (!ok) console.warn("Project onboarding: save before Risk Scale may have failed.");
      else if (fetchProjectProfile) await fetchProjectProfile(project.id);
      setLeadSubStep("risk-scale");
    } finally {
      setQuestionsStepSubmitting(false);
    }
  };
  const submitRiskScaleAndNotifyAdvisor = async () => {
    if (!project?.id || !saveScopingProfile || !updateProjectProfile) return;
    setRiskScaleStepSubmitting(true);
    try {
      const base = profileRef.current || {};
      const riskOnboarding = buildRiskOnboardingPayload(riskScale);
      const prevWizard = base.projectOnboardingWizard && typeof base.projectOnboardingWizard === "object" ? base.projectOnboardingWizard : {};
      const projectOnboardingWizard = {
        ...prevWizard,
        riskScaleComplete: true,
        activeLeadSubStep: "artefact-class",
        advisorArtefactsStatus: "awaiting_advisor",
      };
      const newProfile = {
        ...base,
        riskOnboarding,
        projectOnboardingWizard,
      };
      profileRef.current = newProfile;
      updateProjectProfile(project.id, newProfile);
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (!ok) console.warn("Project onboarding: save after Risk Scale may have failed.");
      else if (fetchProjectProfile) await fetchProjectProfile(project.id);
      setLeadSubStep("artefact-class");
    } finally {
      setRiskScaleStepSubmitting(false);
    }
  };
  const toggleQuestion = (id) => {
    gapQuestionsUserEditedRef.current = true;
    setSelectedQuestions((prev) => prev.map((q) => q.id === id ? { ...q, selected: !q.selected } : q));
  };
  const addQuestionFromBank = (q) => {
    if (selectedQuestions.find((sq) => sq.id === q.id)) return;
    gapQuestionsUserEditedRef.current = true;
    setSelectedQuestions((prev) => [...prev, { ...q, selected: true, source: "bank" }]);
  };
  const removeQuestion = (id) => {
    gapQuestionsUserEditedRef.current = true;
    setSelectedQuestions((prev) => prev.filter((q) => q.id !== id));
  };
  const [editingQId, setEditingQId] = useState(null);
  const [editQText, setEditQText] = useState("");
  const startEditQuestion = (q) => {
    setEditingQId(q.id);
    setEditQText(q.question);
  };
  const saveEditQuestion = () => {
    if (!editingQId) return;
    gapQuestionsUserEditedRef.current = true;
    setSelectedQuestions((prev) => prev.map((q) => q.id === editingQId ? { ...q, question: editQText } : q));
    setEditingQId(null);
    setEditQText("");
  };
  const [showAddQ, setShowAddQ] = useState(false);
  const [newQText, setNewQText] = useState("");
  const [newQDomain, setNewQDomain] = useState("Organization");
  // The response type is never assumed: the Lead Advisor must choose it for a custom question.
  const [newQResponseType, setNewQResponseType] = useState("");
  const responseTypeOptions = useMemo(
    () => [...new Set(["Y / N / NA", ...selectedQuestions.map((q) => q.responseType).filter(Boolean)])],
    [selectedQuestions]
  );
  const addCustomQuestion = () => {
    if (!newQText.trim() || !newQResponseType) return;
    const maxNo = selectedQuestions.reduce((m, q) => Math.max(m, parseFloat(q.qNo) || 0), 0);
    gapQuestionsUserEditedRef.current = true;
    setSelectedQuestions((prev) => [...prev, {
      id: `custom-${Date.now()}`,
      qNo: String(Math.floor(maxNo) + 1),
      question: newQText.trim(),
      domain: newQDomain,
      responseType: newQResponseType,
      selected: true,
      source: "custom",
    }]);
    setNewQText("");
    setNewQResponseType("");
    setShowAddQ(false);
  };
  const docClassRows = useMemo(() => {
    const rows = parseCsv(docClassCsv);
    return rows.slice(1).map((r, i) => ({ id: `dc-${i}`, classificationType: r[0] || "", distributionProtocol: r[1] || "" }));
  }, []);
  const [artefactIndex, setArtefactIndex] = useState(() => {
    const saved = project?.profile?.projectOnboardingWizard?.artefactIndexRows;
    if (!Array.isArray(saved) || saved.length === 0) return [];
    const company = project?.companyName || project?.name || "";
    return normalizeArtefactIndexRows(saved, company).map((r) => ({ ...r }));
  });
  const artefactFileInputRef = useRef(null);
  const plitsAutoPersistSentHydrateKeyRef = useRef(null);
  const [artefactUploadModalOpen, setArtefactUploadModalOpen] = useState(false);
  const [artefactUploadBusy, setArtefactUploadBusy] = useState(false);
  const [pendingArtefactBlobUrl, setPendingArtefactBlobUrl] = useState("");
  const [pendingArtefactFileName, setPendingArtefactFileName] = useState("");
  const [artefactUploadTitle, setArtefactUploadTitle] = useState("");
  const [artefactUploadClassification, setArtefactUploadClassification] = useState("");
  const closeArtefactUploadModal = useCallback(() => {
    setArtefactUploadModalOpen(false);
    setPendingArtefactBlobUrl("");
    setPendingArtefactFileName("");
    setArtefactUploadTitle("");
    setArtefactUploadClassification("");
  }, []);
  const confirmArtefactUploadModal = useCallback(() => {
    const t = artefactUploadTitle.trim();
    if (!t) {
      message.warning("Enter a title for this artefact.");
      return;
    }
    if (!pendingArtefactBlobUrl || !pendingArtefactFileName) {
      message.error("Upload is missing; try again.");
      return;
    }
    const company = project?.companyName || project?.name || "";
    const baseName = pendingArtefactFileName.split(/[/\\]/).pop() || pendingArtefactFileName;
    const displayName = renamePlitsCatalogueTemplateBasename(company, baseName) || baseName;
    setArtefactIndex((prev) => [
      ...prev,
      {
        id: `art-${Date.now()}`,
        name: t,
        templateId: displayName,
        classification: artefactUploadClassification || docClassRows[0]?.classificationType || "",
        blobUrl: pendingArtefactBlobUrl,
        fileName: displayName,
      },
    ]);
    closeArtefactUploadModal();
    message.success("Artefact added.");
  }, [
    artefactUploadTitle,
    artefactUploadClassification,
    pendingArtefactBlobUrl,
    pendingArtefactFileName,
    docClassRows,
    closeArtefactUploadModal,
    project?.companyName,
    project?.name,
  ]);
  const handleArtefactTemplateFileSelected = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!project?.id) {
        message.warning("No project selected.");
        e.target.value = "";
        return;
      }
      setArtefactUploadBusy(true);
      try {
        const token = await getApiToken();
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/artefact-template/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          message.error(json.error?.message || json.message || "Upload failed.");
          return;
        }
        const data = json.data || {};
        const url = data.url;
        const fileName = data.fileName;
        if (!url || !fileName) {
          message.error("Invalid upload response.");
          return;
        }
        const base = (fileName.split(/[/\\]/).pop() || fileName).replace(/\.[^.]+$/, "") || "Artefact";
        setPendingArtefactBlobUrl(url);
        setPendingArtefactFileName(fileName);
        setArtefactUploadTitle(base);
        setArtefactUploadClassification(docClassRows[0]?.classificationType || "");
        setArtefactUploadModalOpen(true);
      } catch (err) {
        console.warn("artefact template upload failed", err);
        message.error(err?.message || "Upload failed.");
      } finally {
        setArtefactUploadBusy(false);
        e.target.value = "";
      }
    },
    [project?.id, getApiToken, docClassRows],
  );
  const updateArtefactRow = (id, field, value) => {
    setArtefactIndex((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value } : r));
  };
  const deleteArtefactRow = (id) => {
    setArtefactIndex((prev) => prev.filter((r) => r.id !== id));
  };
  const [syncingArtefactsFromPolicyHub, setSyncingArtefactsFromPolicyHub] = useState(false);

  /**
   * Persists wizard patch to SQL after save succeeds (no optimistic profile update).
   * @param patch - merged into projectOnboardingWizard
   * @param [options] - optional `errorMessage` overrides default toast when save fails
   * @returns {Promise<boolean>} true if saved
   */
  const persistOnboardingWizardPatch = async (patch, options = {}) => {
    const { errorMessage, impersonated = false } = options;
    if (!project?.id || !saveScopingProfile || !updateProjectProfile) return false;
    const base = profileRef.current || {};
    const newProfile = mergeProjectOnboardingWizard(base, patch);
    const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile), { impersonated });
    if (!ok) {
      console.warn("Project onboarding wizard patch may not have persisted.");
      message.error(
        errorMessage || "Could not save changes. Check your connection and try again.",
      );
      if (fetchProjectProfile) await fetchProjectProfile(project.id);
      return false;
    }
    profileRef.current = newProfile;
    updateProjectProfile(project.id, newProfile);
    return true;
  };
  const [advisorArtefactsSaveBusy, setAdvisorArtefactsSaveBusy] = useState(false);
  const saveAdvisorArtefactClassification = async () => {
    if (!project?.id || !saveScopingProfile || !updateProjectProfile) {
      message.warning("Cannot save right now.");
      return;
    }
    if (advisorArtefactsLocked || onboardingLocked) return;
    setAdvisorArtefactsSaveBusy(true);
    try {
      const rows = artefactIndex.map((r) => ({ ...r }));
      const ok = await persistOnboardingWizardPatch({
        artefactIndexRows: rows,
        activeAdvisorSubStep: "classification",
      });
      if (!ok) return;
      if (fetchProjectProfile) await fetchProjectProfile(project.id);
      message.success("Saved.");
    } finally {
      setAdvisorArtefactsSaveBusy(false);
    }
  };
  const submitAdvisorArtefactsForLdReview = async () => {
    const rows = artefactIndex.map((r) => ({ ...r }));
    await persistOnboardingWizardPatch({
      artefactIndexRows: rows,
      advisorArtefactsStatus: "submitted",
      activeAdvisorSubStep: "classification",
    });
  };
  /**
   * LA "Act on behalf of Advisor" completion — backlog #13. Mirrors
   * OrganizationalSetup.jsx's onSpocApprove*Impersonated shape: (a) not gated on the real
   * `isAdvisor` role (an LA never holds it), (b) requires a live impersonation session already
   * scoped to `advisor` on THIS project, checked fresh at click time, (c) passes
   * `{ impersonated: true }` so the save carries `X-Impersonation-Id`. Never call this for a
   * real advisor — `saveAdvisorArtefactClassification` above stays their only path, unchanged.
   */
  const saveAdvisorArtefactClassificationImpersonated = async () => {
    if (advisorArtefactsLocked || onboardingLocked) return;
    const session = getActiveImpersonation();
    if (!session || session.effectiveRole !== "advisor" || session.projectId !== project?.id) {
      message.warning(
        "Your impersonation session for Advisor is no longer active on this project. Start a new one to continue.",
      );
      return;
    }
    if (!session.effectiveUserId) {
      message.warning(
        "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
      );
      return;
    }
    setAdvisorArtefactsSaveBusy(true);
    try {
      const rows = artefactIndex.map((r) => ({ ...r }));
      const ok = await persistOnboardingWizardPatch(
        { artefactIndexRows: rows, activeAdvisorSubStep: "classification" },
        { impersonated: true },
      );
      if (!ok) return;
      if (fetchProjectProfile) await fetchProjectProfile(project.id);
      message.success("Saved (acting on behalf of Advisor).");
    } finally {
      setAdvisorArtefactsSaveBusy(false);
    }
  };
  /** Sibling of the above — "Submit for LD review" acting on behalf of Advisor. */
  const submitAdvisorArtefactsForLdReviewImpersonated = async () => {
    const session = getActiveImpersonation();
    if (!session || session.effectiveRole !== "advisor" || session.projectId !== project?.id) {
      message.warning(
        "Your impersonation session for Advisor is no longer active on this project. Start a new one to continue.",
      );
      return;
    }
    if (!session.effectiveUserId) {
      message.warning(
        "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
      );
      return;
    }
    const rows = artefactIndex.map((r) => ({ ...r }));
    const ok = await persistOnboardingWizardPatch(
      { artefactIndexRows: rows, advisorArtefactsStatus: "submitted", activeAdvisorSubStep: "classification" },
      { impersonated: true },
    );
    if (ok) message.success("Submitted for your review (acting on behalf of Advisor).");
  };
  const ldApproveAdvisorArtefacts = async () => {
    const ok = await persistOnboardingWizardPatch({
      advisorArtefactsStatus: "approved",
      activeLeadSubStep: "kickoff",
    });
    if (ok) setLeadSubStep("kickoff");
  };
  const ldReturnAdvisorArtefacts = async () => {
    const ok = await persistOnboardingWizardPatch({
      advisorArtefactsStatus: "returned",
      activeLeadSubStep: "artefact-class",
    });
    if (ok) setLeadSubStep("artefact-class");
  };
  const [editingIssue, setEditingIssue] = useState(null);
  const [isQModalEditMode, setIsQModalEditMode] = useState(false);
  const [qModalSections, setQModalSections] = useState({
    companyName: "",
    advisorName: "",
    legalEntities: "",
    productsServices: "",
    activitiesDepts: "",
    otherInfo: "",
    affirmationName: "",
    affirmationDate: ""
  });
  const prepareQuestionnaireSectionsFromProject = () => {
    const defaults = buildGapQuestionnaireScopeFromProject(project, project?.advisoryFirm);
    const savedScope = project?.profile?.gapQuestionnaireDocument?.scope;
    const merged =
      savedScope && typeof savedScope === "object"
        ? { ...defaults, ...savedScope }
        : defaults;
    setQModalSections(merged);
    setIsQModalEditMode(false);
  };
  const goToContractsQuestionnaireStep = async () => {
    prepareQuestionnaireSectionsFromProject();
    const ok = await persistOnboardingWizardPatch({ activeLeadSubStep: "contracts" });
    if (ok) setLeadSubStep("contracts");
  };
  const saveQuestionnaire = async () => {
    if (!project?.id || !saveScopingProfile || !updateProjectProfile) {
      setQuestionnaireFinalized(true);
      setIsQModalEditMode(false);
      return;
    }
    setQuestionnaireSaveBusy(true);
    try {
      const base = profileRef.current || {};
      const gapQuestionnaireDocument = {
        version: 1,
        finalized: true,
        finalizedAt: Date.now(),
        scope: { ...qModalSections },
        riskOnboardingSnapshot: buildRiskOnboardingPayload(riskScale),
      };
      const prevWizard = base.projectOnboardingWizard && typeof base.projectOnboardingWizard === "object" ? base.projectOnboardingWizard : {};
      const projectOnboardingWizard = { ...prevWizard, activeLeadSubStep: "contracts" };
      const newProfile = { ...base, gapQuestionnaireDocument, projectOnboardingWizard };
      profileRef.current = newProfile;
      updateProjectProfile(project.id, newProfile);
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (!ok) console.warn("Gap questionnaire document may not have persisted to the server.");
      setQuestionnaireFinalized(true);
      setIsQModalEditMode(false);
    } finally {
      setQuestionnaireSaveBusy(false);
    }
  };
  const [questionnaireFinalized, setQuestionnaireFinalized] = useState(false);
  const [questionnaireSaveBusy, setQuestionnaireSaveBusy] = useState(false);
  const gapQDocSnapshot = useMemo(
    () => JSON.stringify(project?.profile?.gapQuestionnaireDocument ?? null),
    [project?.profile?.gapQuestionnaireDocument],
  );
  useEffect(() => {
    const doc = project?.profile?.gapQuestionnaireDocument;
    setQuestionnaireFinalized(!!(doc && doc.finalized === true));
  }, [project?.id, gapQDocSnapshot]);
  useEffect(() => {
    if (leadSubStep !== "contracts") return;
    prepareQuestionnaireSectionsFromProject();
  }, [leadSubStep, project?.id]);
  useEffect(() => {
    if (!isSPOC) return;
    prepareQuestionnaireSectionsFromProject();
  }, [isSPOC, project?.id, gapQDocSnapshot]);
  useEffect(() => {
    if (!isAdvisor) return;
    if (advisorSubStep !== "contracts") return;
    prepareQuestionnaireSectionsFromProject();
  }, [isAdvisor, advisorSubStep, project?.id, gapQDocSnapshot]);
  const onboardingLocked = questionnaireFinalized || step3Status === "Finalized";
  const [kickoffScheduled, setKickoffScheduled] = useState(false);
  const [kickoffStartLocal, setKickoffStartLocal] = useState("");
  const [kickoffNotes, setKickoffNotes] = useState("");
  const [kickoffTranscripts, setKickoffTranscripts] = useState([]);
  const [kickoffUploadBusy, setKickoffUploadBusy] = useState(false);
  const [kickoffScheduleBusy, setKickoffScheduleBusy] = useState(false);
  const [kickoffCalendarGuestEmails, setKickoffCalendarGuestEmails] = useState([]);
  useEffect(() => {
    if (!project?.id || !isLeadAdvisor || !fetchProjectMembers) {
      setKickoffCalendarGuestEmails([]);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      const members = await fetchProjectMembers(project.id);
      if (cancelled) return;
      const emails = (members || []).map((m) => m.email).filter(Boolean);
      setKickoffCalendarGuestEmails(emails);
    })();
    return () => {
      cancelled = true;
    };
  }, [project?.id, isLeadAdvisor, fetchProjectMembers]);
  const persistKickoffWizard = async () => {
    if (!kickoffStartLocal || !project?.id) return;
    const kickoffStartAt = datetimeLocalValueToIso(kickoffStartLocal);
    if (!kickoffStartAt) return;
    if (kickoffScheduleBusy) return;
    setKickoffScheduleBusy(true);
    try {
      const w = project?.profile?.projectOnboardingWizard;
      const title = `Gap analysis kick-off - ${project?.name || project?.companyName || "Project"}`;
      /** Only reuse a stored meeting id when kick-off was already scheduled; otherwise a stale id skips POST /meetings. */
      const existingMeetingId =
        w?.kickoffScheduled === true && w?.kickoffMeetingId ? w.kickoffMeetingId : undefined;
      let meetingId = existingMeetingId;
      let createdMeetingInThisCall = false;

      if (!meetingId) {
        if (!createMeeting) {
          message.error("Cannot schedule meeting: sign in and try again.");
          return;
        }
        const members = await fetchProjectMembers(project.id);
        const attendees = (members || [])
          .filter((m) => m.email)
          .map((m) => ({
            userId: m.id,
            name: m.name || m.email,
            email: m.email,
            role: m.role,
          }));
        const meeting = await createMeeting(project.id, {
          title,
          meetingDate: kickoffStartAt,
          meetingType: "remote_video",
          notes: kickoffNotes || "",
          attendees,
        });
        if (!meeting?.id) {
          message.error("Could not save the meeting. Check your connection and try again.");
          return;
        }
        meetingId = meeting.id;
        createdMeetingInThisCall = true;
      }

      // Wire value: the kickoff day in the scheduler's own (browser) zone, matching the datetime-local input; intentionally not IST.
      const d = new Date(kickoffStartAt);
      const pad = (n) => String(n).padStart(2, "0");
      const kickoffDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const patchOk = await persistOnboardingWizardPatch(
        {
          kickoffScheduled: true,
          kickoffStartAt,
          kickoffDate,
          kickoffNotes,
          ...(meetingId ? { kickoffMeetingId: meetingId } : {}),
          activeLeadSubStep: "review",
        },
        {
          errorMessage: createdMeetingInThisCall
            ? "Meeting was created in PolicyLens but saving the workbench step failed. Try Schedule Kick-off again."
            : undefined,
        },
      );
      if (!patchOk) return;
      setKickoffScheduled(true);
      setLeadSubStep("review");
      if (fetchProjectProfile) await fetchProjectProfile(project.id);
      if (fetchNotifications) fetchNotifications();
      message.success("Kick-off scheduled. Project members can see it under Meetings.");
    } finally {
      setKickoffScheduleBusy(false);
    }
  };

  /** Legacy: wizard had kickoff saved without a DB meeting — create once so Meetings + notifications work. */
  useEffect(() => {
    if (!isLeadAdvisor || !project?.id || !createMeeting || !fetchProjectMembers) return;
    const w = project?.profile?.projectOnboardingWizard;
    if (!w?.kickoffScheduled || w.kickoffMeetingId || !w.kickoffStartAt) return;
    const storageKey = `pl-kickoff-meeting-sync-${project.id}`;
    try {
      if (typeof sessionStorage !== "undefined") {
        if (sessionStorage.getItem(storageKey) === "done") return;
        if (sessionStorage.getItem(storageKey) === "pending") return;
        sessionStorage.setItem(storageKey, "pending");
      }
    } catch {
      /* ignore */
    }
    void (async () => {
      try {
        const members = await fetchProjectMembers(project.id);
        const attendees = (members || [])
          .filter((m) => m.email)
          .map((m) => ({
            userId: m.id,
            name: m.name || m.email,
            email: m.email,
            role: m.role,
          }));
        const title = `Gap analysis kick-off - ${project?.name || project?.companyName || "Project"}`;
        const meeting = await createMeeting(project.id, {
          title,
          meetingDate: w.kickoffStartAt,
          meetingType: "remote_video",
          notes: typeof w.kickoffNotes === "string" ? w.kickoffNotes : "",
          attendees,
        });
        if (!meeting?.id) {
          try {
            if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(storageKey);
          } catch {
            /* ignore */
          }
          return;
        }
        const patchOk = await persistOnboardingWizardPatch({ kickoffMeetingId: meeting.id });
        if (!patchOk) {
          try {
            if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(storageKey);
          } catch {
            /* ignore */
          }
          return;
        }
        try {
          if (typeof sessionStorage !== "undefined") sessionStorage.setItem(storageKey, "done");
        } catch {
          /* ignore */
        }
        if (fetchProjectProfile) await fetchProjectProfile(project.id);
        if (fetchNotifications) fetchNotifications();
        message.success("Kick-off synced to Meetings for all project members.");
      } catch (e) {
        console.warn("Kick-off meeting backfill failed", e);
        try {
          if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(storageKey);
        } catch {
          /* ignore */
        }
      }
    })();
  }, [
    isLeadAdvisor,
    project?.id,
    project?.name,
    project?.companyName,
    project?.profile?.projectOnboardingWizard?.kickoffScheduled,
    project?.profile?.projectOnboardingWizard?.kickoffMeetingId,
    project?.profile?.projectOnboardingWizard?.kickoffStartAt,
    project?.profile?.projectOnboardingWizard?.kickoffNotes,
    createMeeting,
    fetchProjectMembers,
    fetchProjectProfile,
    fetchNotifications,
  ]);
  const uploadKickoffTranscriptFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !project?.id) return;
    setKickoffUploadBusy(true);
    try {
      const token = await getApiToken();
      const fd = new FormData();
      fd.append("file", file);
      const base = process.env.REACT_APP_API_BASE_URL || "";
      const res = await fetch(`${base}/api/v1/projects/${project.id}/kickoff-transcript/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        console.warn("Kickoff transcript upload failed");
        return;
      }
      const d = json.data || {};
      const url = d.url;
      const fileName = d.fileName;
      // The backend's tenant-aware SAS signer can legitimately return a null url
      // (cross-tenant denial or an Azure credential-parse failure). Do not persist
      // a broken entry into wizard state on that path — a null here would otherwise
      // survive reload as a permanently dead, non-clickable link (see lesson
      // audit-log-unconditional-success-before-persisted-check: never report/persist
      // success for something that did not actually happen).
      if (!url || !fileName) {
        message.error("File uploaded, but a secure link could not be generated. Please try uploading again.");
        return;
      }
      const entry = {
        id: `kt-${d.uploadedAt}-${Math.random().toString(36).slice(2, 9)}`,
        fileName,
        url,
        uploadedAt: d.uploadedAt,
      };
      const next = [...kickoffTranscripts, entry];
      setKickoffTranscripts(next);
      await persistOnboardingWizardPatch({ kickoffTranscripts: next });
    } finally {
      setKickoffUploadBusy(false);
    }
  };
  const removeKickoffTranscript = async (id) => {
    const next = kickoffTranscripts.filter((t) => t.id !== id);
    setKickoffTranscripts(next);
    await persistOnboardingWizardPatch({ kickoffTranscripts: next });
  };
  const [qSearch, setQSearch] = useState("");
  const [bankPanelSearch, setBankPanelSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState("All");
  const domainMatchesFilter = (rowDomain, filterVal) => {
    if (filterVal === "All") return true;
    const a = (rowDomain || "").trim().toLowerCase();
    const b = (filterVal || "").trim().toLowerCase();
    return a === b;
  };
  const rowMatchesSearch = (row, searchRaw) => {
    const q = (searchRaw || "").trim().toLowerCase();
    if (!q) return true;
    const hay = `${row.question || ""} ${row.qNo || ""} ${row.domain || ""}`.toLowerCase();
    return hay.includes(q);
  };
  const filteredBankQuestions = useMemo(() => {
    return bankQuestions.filter((q) => {
      if (!domainMatchesFilter(q.domain, domainFilter)) return false;
      if (!rowMatchesSearch(q, bankPanelSearch)) return false;
      return true;
    });
  }, [bankQuestions, bankPanelSearch, domainFilter]);
  /** Filters the main AI-selected / questionnaire table (same controls as toolbar). */
  const filteredSelectedQuestions = useMemo(() => {
    return selectedQuestions.filter((q) => {
      if (!domainMatchesFilter(q.domain, domainFilter)) return false;
      if (!rowMatchesSearch(q, qSearch)) return false;
      return true;
    });
  }, [selectedQuestions, domainFilter, qSearch]);
  const domains = useMemo(() => {
    const fromMeta = [...bankMetaDomains];
    const fromBank = [...new Set(bankQuestions.map((q) => q.domain).filter(Boolean))];
    const fromSelected = [...new Set(selectedQuestions.map((q) => q.domain).filter(Boolean))];
    return [...new Set([...fromMeta, ...fromBank, ...fromSelected])].sort();
  }, [bankMetaDomains, bankQuestions, selectedQuestions]);
  const domainOptionsForCustom = useMemo(() => {
    if (domains.length) return domains;
    return ["Organization", "IT / IT enabled", "People", "Physical", "General"];
  }, [domains]);
  const selectedIds = useMemo(() => new Set(selectedQuestions.map((q) => q.id)), [selectedQuestions]);
  const availableFromBankCount = useMemo(
    () => bankQuestions.filter((q) => !selectedIds.has(q.id)).length,
    [bankQuestions, selectedIds],
  );
  const aiPickedCount = useMemo(
    () => selectedQuestions.filter((q) => q.source === "ai").length,
    [selectedQuestions],
  );
  const domainsInAiSelection = useMemo(() => {
    const set = new Set(
      selectedQuestions.filter((q) => q.source === "ai").map((q) => q.domain).filter(Boolean),
    );
    return set.size;
  }, [selectedQuestions]);
  /** Persisted labels preferred on Review & Finalize; falls back to live AI-sourced rows before debounced save completes. */
  const reviewAiBatchDomainLine = useMemo(() => {
    const labels = project?.profile?.gapAnalysisQuestionnaire?.aiDomainLabels;
    if (Array.isArray(labels) && labels.length > 0) {
      return labels.join(", ");
    }
    const live = [
      ...new Set(
        selectedQuestions.filter((q) => q.source === "ai").map((q) => q.domain).filter(Boolean),
      ),
    ].sort();
    return live.length ? live.join(", ") : "";
  }, [project?.profile?.gapAnalysisQuestionnaire, selectedQuestions]);
  const classificationColors = {
    "Public": "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]",
    "Internal - may leave the organization": "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]",
    "Internal - may not leave the organization": "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]",
    "Confidential": "bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border-[color:var(--color-warn-line)]",
    "Confidential - Sensitive": "bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border-[color:var(--color-danger-line)]"
  };
  /** Short badge text for document classification criteria reference table (keys match `docClassCsv`). */
  const classificationBadgeShortLabel = {
    Public: "PUBLIC",
    "Internal - may leave the organization": "INTERNAL",
    "Internal - may not leave the organization": "INTERNAL",
    Confidential: "CONFIDENTIAL",
    "Confidential - Sensitive": "CONFIDENTIAL",
  };
  const LEAD_SUBSTEPS = [
    { key: "questions", label: "Select Questions", num: 1 },
    { key: "risk-scale", label: "Risk Scale", num: 2 },
    { key: "artefact-class", label: "Artefacts Index", num: 3 },
    { key: "kickoff", label: "Kick-off Meeting", num: 4 },
    { key: "review", label: "Review & Finalize", num: 5 },
    { key: "contracts", label: "Gap analysis draft", num: 6 },
  ];
  const ADVISOR_SUBSTEPS = [
    { key: "classification", label: "Artefacts Index", num: 1 },
    { key: "contracts", label: "Gap analysis draft", num: 2 },
  ];
  const wizardState = project?.profile?.projectOnboardingWizard;
  const advisorArtefactsStatus = wizardState?.advisorArtefactsStatus;
  const questionsStepComplete = !!wizardState?.selectQuestions;
  const riskScaleStepComplete = !!wizardState?.riskScaleComplete;
  /** Revisit after Submit & continue: show saved data without AI / submit (mirrors scoping sub-tabs). */
  const leadUiLockedQuestions =
    onboardingLocked || (leadSubStep === "questions" && questionsStepComplete);
  const leadUiLockedRiskScale =
    onboardingLocked || (leadSubStep === "risk-scale" && riskScaleStepComplete);
  const leadUiLockedKickoff =
    onboardingLocked || (leadSubStep === "kickoff" && !!wizardState?.kickoffScheduled);
  const artefactRowsFromProfile = Array.isArray(wizardState?.artefactIndexRows) ? wizardState.artefactIndexRows : null;
  /** Keep advisor tabs visible after submit and after LD approval (read-only); otherwise the panel disappears once status becomes `approved`. */
  const showAdvisorTrack =
    isAdvisor &&
    wizardState?.riskScaleComplete &&
    (advisorArtefactsStatus === "awaiting_advisor" ||
      advisorArtefactsStatus === "returned" ||
      advisorArtefactsStatus === "submitted" ||
      advisorArtefactsStatus === "approved");
  const advisorArtefactsLocked =
    advisorArtefactsStatus === "submitted" || advisorArtefactsStatus === "approved";
  const syncArtefactRowsFromPolicyHub = useCallback(async () => {
    if (advisorArtefactsLocked || onboardingLocked) return;
    const defaultClass = docClassRows[0]?.classificationType || "";
    setSyncingArtefactsFromPolicyHub(true);
    try {
      const token = await getApiToken();
      const tplRes = await fetch(`${apiConfig.baseUrl}/api/v1/artefact-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const tplJson = await tplRes.json();
      if (!tplRes.ok || !tplJson.success) {
        message.error(tplJson.error?.message || "Could not load artefact templates.");
        return;
      }
      const files = tplJson.data?.files || [];
      if (files.length === 0) {
        message.warning("No artefact template files returned from storage.");
        return;
      }
      const company = project?.companyName || project?.name || "";
      const rows = buildArtefactRowsFromBlobCatalog(files, defaultClass, company);
      setArtefactIndex(rows);
      message.success(`Imported ${rows.length} artefact row(s) from catalogue storage.`);
    } catch (e) {
      console.warn("Artefact catalogue import failed", e);
      message.error(e?.message || "Failed to import artefact catalogue.");
    } finally {
      setSyncingArtefactsFromPolicyHub(false);
    }
  }, [
    advisorArtefactsLocked,
    onboardingLocked,
    project?.companyName,
    project?.name,
    docClassRows,
    getApiToken,
  ]);

  // --- AI document-naming assist (backlog #14, R016) --------------------------------------
  // POST /api/v1/projects/:id/artefact-index/name-suggestions. Commandment IV: suggestions are
  // rendered for explicit human pick + "Apply" — never auto-written into the row. The backend
  // response does NOT include a `confidence` or `reasoningRefs` field even though the AI wrapper
  // computes both (see .claude/lessons/ai-response-dto-drops-confidence-reasoningrefs.md) — this
  // is a flagged, unfixed backend gap, not something the UI fabricates a value for.
  const [aiNamingModal, setAiNamingModal] = useState(null);
  const requestAiNameSuggestions = useCallback(
    async (row) => {
      if (advisorArtefactsLocked || onboardingLocked) return;
      if (!project?.id) {
        message.warning("No project selected.");
        return;
      }
      const classification = row.classification || docClassRows[0]?.classificationType || "";
      if (!classification) {
        message.warning("Set a classification for this artefact before requesting AI name suggestions.");
        return;
      }
      setAiNamingModal({
        rowId: row.id,
        loading: true,
        error: null,
        suggestions: [],
        warnings: [],
        conventionPattern: "",
        conventionPatternSource: "",
        meta: null,
        selectedName: null,
      });
      try {
        const token = await getApiToken();
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${project.id}/artefact-index/name-suggestions`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ classification }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          const msg = json.error?.message || json.message || "AI name suggestions failed.";
          setAiNamingModal((prev) => (prev && prev.rowId === row.id ? { ...prev, loading: false, error: msg } : prev));
          return;
        }
        const data = json.data || {};
        setAiNamingModal((prev) =>
          prev && prev.rowId === row.id
            ? {
                ...prev,
                loading: false,
                suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
                warnings: Array.isArray(data.warnings) ? data.warnings : [],
                conventionPattern: data.conventionPattern || "",
                conventionPatternSource: data.conventionPatternSource || "",
                meta: data.meta || null,
              }
            : prev,
        );
      } catch (err) {
        console.warn("AI name suggestions failed", err);
        setAiNamingModal((prev) =>
          prev && prev.rowId === row.id ? { ...prev, loading: false, error: err?.message || "AI name suggestions failed." } : prev,
        );
      }
    },
    [project?.id, getApiToken, advisorArtefactsLocked, onboardingLocked, docClassRows],
  );
  // Commandment IV: capture the human accept/reject of AI name suggestions as an audit event.
  // Best-effort: a failure here must never block the user's edit.
  const recordAiNameDecision = useCallback(
    async (modal, decision, chosenName) => {
      const requestId = modal?.meta?.requestId;
      if (!project?.id || !requestId || !Array.isArray(modal.suggestions) || modal.suggestions.length === 0) return;
      try {
        const token = await getApiToken();
        await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/artefact-index/name-suggestions/decision`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            decision,
            requestId,
            chosenName: chosenName || undefined,
            suggestedNames: modal.suggestions.map((x) => x?.name).filter(Boolean),
          }),
        });
      } catch (err) {
        console.warn("Could not record AI name decision", err);
      }
    },
    [project?.id, getApiToken],
  );
  const closeAiNamingModal = useCallback(() => {
    void recordAiNameDecision(aiNamingModal, "rejected");
    setAiNamingModal(null);
  }, [aiNamingModal, recordAiNameDecision]);
  const applyAiNameSuggestion = useCallback(() => {
    if (!aiNamingModal || !aiNamingModal.selectedName) return;
    void recordAiNameDecision(aiNamingModal, "accepted", aiNamingModal.selectedName);
    updateArtefactRow(aiNamingModal.rowId, "name", aiNamingModal.selectedName);
    message.success("AI-suggested name applied - remember to Save.");
    setAiNamingModal(null);
  }, [aiNamingModal, recordAiNameDecision]);

  const ldArtefactRowsForLd = useMemo(() => {
    const raw =
      Array.isArray(artefactRowsFromProfile) && artefactRowsFromProfile.length > 0
        ? artefactRowsFromProfile
        : artefactIndex;
    const company = project?.companyName || project?.name || "";
    return normalizeArtefactIndexRows(raw, company);
  }, [
    artefactRowsFromProfile,
    artefactIndex,
    project?.companyName,
    project?.name,
  ]);
  const googleCalendarKickoffUrl = useMemo(() => {
    if (!kickoffStartLocal) return "";
    const iso = datetimeLocalValueToIso(kickoffStartLocal);
    if (!iso) return "";
    const start = new Date(iso);
    const title = `Gap analysis kick-off - ${project?.name || project?.companyName || "Project"}`;
    const details = (kickoffNotes || "").slice(0, 4e3);
    return buildGoogleCalendarTemplateUrl({
      title,
      details,
      start,
      end: new Date(start.getTime() + 60 * 60 * 1000),
      guestEmails: kickoffCalendarGuestEmails,
    });
  }, [kickoffStartLocal, kickoffNotes, project?.name, project?.companyName, kickoffCalendarGuestEmails]);
  const artefactRowsHydrateKey = useMemo(
    () => JSON.stringify(project?.profile?.projectOnboardingWizard?.artefactIndexRows ?? null),
    [project?.profile?.projectOnboardingWizard?.artefactIndexRows],
  );
  useEffect(() => {
    plitsAutoPersistSentHydrateKeyRef.current = null;
  }, [project?.id, project?.companyName, project?.name]);
  /**
   * Backlog #13 follow-up (Engineering Director / Security Reviewer BLOCK on `97bec8a`):
   * this legacy-row-format migration below is a passive background effect, not an explicit
   * user action — it must not attempt an `artefactIndexRows` write the backend's advisor-only
   * gate (`assertProjectOnboardingWizardSaveAllowed`, registerRouteHelpers.ts) will reject.
   * Before this guard, ANY non-advisor viewer (a Lead Advisor with no active impersonation
   * session, a Company SPOC, a Company Delegate) opening this screen with legacy-format rows
   * present would silently trigger this write, get a 403, and see a spurious
   * "Could not save changes" toast on every visit — with no action available to them to fix
   * it — plus a wasted `PROJECT_ONBOARDING_WIZARD_FIELD_ACCESS_DENIED` audit row each time
   * (Commandment VI: audit noise from a write nobody asked for). Fix: only a genuine
   * (non-impersonated) Advisor or platform admin can trigger this unattended migration write;
   * everyone else still gets the corrected LOCAL display via `normalizeArtefactIndexRows`
   * above, just without persisting it, until an authorized advisor next saves the screen.
   * Impersonation is deliberately excluded here too — this background effect never passes
   * `{ impersonated: true }`, so firing it during an active "act on behalf of Advisor" session
   * would 403 anyway (no impersonation headers attached) and would additionally be an
   * unattended write happening mid-impersonation with no explicit LA action behind it.
   */
  const canAutoPersistArtefactIndexRows = isAdvisor || user?.role === UserRole.ADMIN;
  useEffect(() => {
    const rows = project?.profile?.projectOnboardingWizard?.artefactIndexRows;
    if (!Array.isArray(rows) || rows.length === 0) return;
    const company = project?.companyName || project?.name || "";
    const normalized = normalizeArtefactIndexRows(rows, company);
    setArtefactIndex(normalized.map((r) => ({ ...r })));

    const changed = normalized.some((n, i) => JSON.stringify(n) !== JSON.stringify(rows[i]));
    if (!changed || onboardingLocked) return;
    if (!canAutoPersistArtefactIndexRows) return;
    if (plitsAutoPersistSentHydrateKeyRef.current === artefactRowsHydrateKey) return;
    plitsAutoPersistSentHydrateKeyRef.current = artefactRowsHydrateKey;
    void (async () => {
      const ok = await persistOnboardingWizardPatch({ artefactIndexRows: normalized });
      if (!ok) plitsAutoPersistSentHydrateKeyRef.current = null;
    })();
  }, [
    project?.id,
    project?.companyName,
    project?.name,
    artefactRowsHydrateKey,
    onboardingLocked,
    canAutoPersistArtefactIndexRows,
  ]);
  useEffect(() => {
    const w = project?.profile?.projectOnboardingWizard;
    if (!w || typeof w !== "object") return;
    if (typeof w.kickoffScheduled === "boolean") setKickoffScheduled(w.kickoffScheduled);
    if (typeof w.kickoffStartAt === "string" && w.kickoffStartAt) {
      setKickoffStartLocal(isoToDatetimeLocalValue(w.kickoffStartAt));
    } else if (typeof w.kickoffDate === "string" && w.kickoffDate) {
      setKickoffStartLocal(`${w.kickoffDate}T09:00`);
    }
    if (typeof w.kickoffNotes === "string") setKickoffNotes(w.kickoffNotes);
    if (Array.isArray(w.kickoffTranscripts)) {
      setKickoffTranscripts(w.kickoffTranscripts.map((t) => ({ ...t })));
    } else {
      setKickoffTranscripts([]);
    }
  }, [
    project?.id,
    project?.profile?.projectOnboardingWizard?.kickoffScheduled,
    project?.profile?.projectOnboardingWizard?.kickoffStartAt,
    project?.profile?.projectOnboardingWizard?.kickoffDate,
    project?.profile?.projectOnboardingWizard?.kickoffNotes,
    project?.profile?.projectOnboardingWizard?.kickoffTranscripts,
  ]);
  const artefactPackSubmittedLd =
    advisorArtefactsStatus === "submitted" || advisorArtefactsStatus === "approved";
  const isLeadSubStepComplete = (key) => {
    switch (key) {
      case "questions":
        return !!wizardState?.selectQuestions;
      case "risk-scale":
        return !!wizardState?.riskScaleComplete;
      case "artefact-class":
        return artefactPackSubmittedLd;
      case "kickoff":
        return !!wizardState?.kickoffScheduled;
      case "review":
        return wizardState?.activeLeadSubStep === "contracts" || questionnaireFinalized;
      case "contracts":
        return questionnaireFinalized;
      default:
        return false;
    }
  };
  const isAdvisorSubStepComplete = (key) => {
    switch (key) {
      case "classification":
        return artefactPackSubmittedLd;
      case "contracts":
        return questionnaireFinalized || step3Status === "Finalized";
      default:
        return false;
    }
  };
  const leadOnboardingAttentionKey = useMemo(() => {
    if (!isLeadAdvisor || onboardingLocked) return null;
    for (const s of LEAD_SUBSTEPS) {
      if (!isLeadSubStepComplete(s.key)) return s.key;
    }
    return null;
  }, [
    isLeadAdvisor,
    onboardingLocked,
    wizardState?.selectQuestions,
    wizardState?.riskScaleComplete,
    wizardState?.kickoffScheduled,
    wizardState?.activeLeadSubStep,
    artefactPackSubmittedLd,
    questionnaireFinalized,
  ]);
  const advisorOnboardingAttentionKey = useMemo(() => {
    if (!showAdvisorTrack || onboardingLocked) return null;
    for (const s of ADVISOR_SUBSTEPS) {
      if (!isAdvisorSubStepComplete(s.key)) return s.key;
    }
    return null;
  }, [showAdvisorTrack, onboardingLocked, artefactPackSubmittedLd, questionnaireFinalized, step3Status]);
  const showLeadAdvisorTrack = isLeadAdvisor;
  return /* @__PURE__ */ jsxs("section", {
    className: "space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700", children: [
    /* @__PURE__ */ jsxs("div", {
      className: "flex items-center justify-between flex-wrap gap-3", children: [
      /* @__PURE__ */ jsxs("div", {
        className: "flex items-center gap-4", children: [
        /* @__PURE__ */ jsx(StageHeader, { title: "Project Onboarding" }),
        /* @__PURE__ */ jsx("span", { className: `px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${step3StatusForUi === "Draft" ? "bg-[color:var(--color-bg-alt)] text-white/80 border-[color:var(--color-border)]" : step3StatusForUi === "Under Review" ? "bg-[color:var(--color-accent-tint)] text-white/80 border-[color:var(--color-info-line)] animate-pulse" : step3StatusForUi === "Approved by SPOC" ? "bg-[color:var(--color-ok-bg)] text-white/80 border-[color:var(--color-ok-line)]" : "bg-[color:var(--color-accent-fill)] text-white border-[color:var(--color-accent-fill)]"}`, children: step3StatusForUi })
        ]
      }),
      /* @__PURE__ */ jsxs("div", {
        className: "flex items-center gap-2", children: [
          isSPOC && step3Status === "Under Review" && /* @__PURE__ */ jsxs(Fragment, {
            children: [
          /* @__PURE__ */ jsx("button", { onClick: () => openOnboardingReviewModal("Draft"), className: "px-4 py-2 bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border border-[color:var(--color-danger-line)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-danger-bg)] transition-all shadow-sm", children: "Send for Re-review" }),
          /* @__PURE__ */ jsx("button", { onClick: () => openOnboardingReviewModal("Approved by SPOC"), className: "px-4 py-2 bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-80 transition-all shadow-lg", children: "Approve" })
            ]
          }),
          (isLeadAdvisor || isAdvisor) && step3Status === "Approved by SPOC" && /* @__PURE__ */ jsxs("button", {
            onClick: () => persistOnboardingReviewStatus("Finalized"), className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M5 13l4 4L19 7" }) }),
              "Publish"
            ]
          })
        ]
      })
      ]
    }),
      // Task #128 fast-follow — Regulatory Role (DPDP/GDPR controller/processor/both).
      // Mirrors the API's ABAC exactly (CLAUDE.md §6) — see canDraftRegulatoryRole /
      // canConfirmRegulatoryRole above. Company Delegate matches neither: full absence,
      // no read-only fallback, matching the Stage 3+ delegate convention.
      (canDraftRegulatoryRole || canConfirmRegulatoryRole) && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-6 py-5 shadow-sm flex flex-wrap items-center justify-between gap-4",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 flex-wrap", children: [
            /* @__PURE__ */ jsx("span", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Regulatory Role" }),
            /* @__PURE__ */ jsx("span", {
              className: `px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
                project?.regulatoryRoleConfirmed
                  ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]"
                  : project?.regulatoryRole
                    ? "bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border-[color:var(--color-warn-line)]"
                    : "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] border-[color:var(--color-border)]"
              }`,
              children: project?.regulatoryRoleConfirmed
                ? `Confirmed - ${REGULATORY_ROLE_OPTIONS.find((o) => o.value === project.regulatoryRole)?.label || project.regulatoryRole}`
                : project?.regulatoryRole
                  ? `Draft - ${REGULATORY_ROLE_OPTIONS.find((o) => o.value === project.regulatoryRole)?.label || project.regulatoryRole}`
                  : "Not set",
            }),
            project?.regulatoryRoleConfirmed && /* @__PURE__ */ jsxs("span", {
              className: "text-[10px] text-[color:var(--color-text-muted)] font-medium",
              children: [
                "Confirmed by ",
                project.regulatoryRoleConfirmedBy || "-",
                " on ",
                formatDdMmYyyy(project.regulatoryRoleConfirmedAt),
              ],
            }),
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
            canDraftRegulatoryRole && /* @__PURE__ */ jsxs(Fragment, {
              children: [
                /* @__PURE__ */ jsxs("select", {
                  value: regulatoryRoleDraft,
                  onChange: (e) => setRegulatoryRoleDraft(e.target.value),
                  disabled: regulatoryRoleSaving,
                  className: "bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-1.5 text-xs font-bold text-[color:var(--color-text-hero)] outline-none disabled:opacity-50 disabled:cursor-not-allowed",
                  children: [
                    /* @__PURE__ */ jsx("option", { value: "", disabled: true, children: "Select role..." }),
                    REGULATORY_ROLE_OPTIONS.map((o) => /* @__PURE__ */ jsx("option", { value: o.value, children: o.label }, o.value)),
                  ],
                }),
                /* @__PURE__ */ jsx("button", {
                  type: "button",
                  onClick: () => void saveRegulatoryRoleDraft(),
                  disabled:
                    regulatoryRoleSaving ||
                    !regulatoryRoleDraft ||
                    regulatoryRoleDraft === (project?.regulatoryRole || ""),
                  className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] shadow-sm",
                  children: regulatoryRoleSaving ? "Saving…" : "Save Draft",
                }),
                project?.regulatoryRoleConfirmed && /* @__PURE__ */ jsx("span", {
                  className: "w-full text-[10px] text-[color:var(--color-warn-fg)] font-bold mt-1",
                  children: "Saving a new draft will un-confirm the currently confirmed role.",
                }),
              ],
            }),
            canConfirmRegulatoryRole && project?.regulatoryRole && !project?.regulatoryRoleConfirmed && /* @__PURE__ */ jsx("button", {
              type: "button",
              onClick: () => void confirmRegulatoryRole(),
              disabled: regulatoryRoleConfirming,
              className: "px-4 py-2 bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-80 transition-all disabled:opacity-50 shadow-lg",
              children: regulatoryRoleConfirming ? "Confirming…" : "Confirm",
            }),
          ] }),
        ],
      }),
      renderOnboardingStatusBar(step3StatusForUi),
      showLeadAdvisorTrack && /* @__PURE__ */ jsxs("div", {
        className: "space-y-6", children: [
          /* @__PURE__ */ jsx(SubStepNav, {
            className: "mb-6",
            steps: LEAD_SUBSTEPS.map((s) => ({
              ...s,
              completed: isLeadSubStepComplete(s.key),
              attentionTitle: leadOnboardingAttentionKey === s.key ? "Your next step - continue project onboarding here" : undefined,
            })),
            activeKey: leadSubStep,
            onSelect: (k) => {
              setLeadSubStep(k);
              void persistActiveLeadSubStep(k);
            },
          }),
          onboardingLocked && /* @__PURE__ */ jsx("div", {
            className: "rounded-2xl border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-6 py-4 text-sm text-[color:var(--color-ok-fg)] font-medium",
            children:
              step3Status === "Finalized"
                ? "Contract published - project onboarding is locked for everyone."
                : "Questionnaire finalized - this onboarding flow is read-only. Editing and saving are disabled.",
          }),
          leadSubStep === "questions" && /* @__PURE__ */ jsxs("div", {
            className: "space-y-4", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "grid grid-cols-1 md:grid-cols-3 gap-4", children: [
          /* @__PURE__ */ jsxs("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-2xl border p-5 shadow-sm", children: [
            /* @__PURE__ */ jsx("div", { className: "text-xs font-black uppercase text-[color:var(--color-text-muted)]", children: "Total from Bank" }),
            /* @__PURE__ */ jsx("div", { className: "text-3xl font-black mt-1", children: bankLoading || qbVersionsLoading ? "…" : totalFromBank ?? "-" }),
            /* @__PURE__ */ jsx("div", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "ACTIVE rows in SQL question bank" })
                ]
              }),
          /* @__PURE__ */ jsxs("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-2xl border p-5 shadow-sm", children: [
            /* @__PURE__ */ jsx("div", { className: "text-xs font-black uppercase text-[color:var(--color-text-muted)]", children: "AI selected" }),
            /* @__PURE__ */ jsx("div", { className: "text-3xl font-black mt-1 text-[color:var(--color-accent)]", children: AI_GAP_TARGET }),
            /* @__PURE__ */ jsxs("div", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: [
                  "Current: ",
                  /* @__PURE__ */ jsx("span", { className: "font-black text-[color:var(--color-text-hero)]", children: aiPickedCount }),
                  " from AI run"
                ] })
                ]
              }),
          /* @__PURE__ */ jsxs("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-2xl border p-5 shadow-sm", children: [
            /* @__PURE__ */ jsx("div", { className: "text-xs font-black uppercase text-[color:var(--color-text-muted)]", children: "Domains" }),
            /* @__PURE__ */ jsx("div", { className: "text-3xl font-black mt-1", children: aiPickedCount > 0 ? domainsInAiSelection : "-" }),
            /* @__PURE__ */ jsxs("div", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: [
                  "Distinct domains represented in the ",
                  AI_GAP_TARGET,
                  "-question AI batch (",
                  bankMetaDomains.length || domains.length || "-",
                  " domain labels in bank)"
                ] })
                ]
              })
              ]
            }),
            (bankError || qbVersionsError) && /* @__PURE__ */ jsx("div", { className: "rounded-xl border border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] text-sm px-4 py-3", children: bankError || qbVersionsError }),
            aiError && /* @__PURE__ */ jsx("div", { className: "rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] text-sm px-4 py-3", children: aiError }),
            degradedStandards.length > 0 && /* @__PURE__ */ jsx(GapAnalysisDegradedStandardsBanner, { degradedStandards }),
        /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
          /* @__PURE__ */ jsxs("div", {
                className: "px-8 py-5 border-b border-[color:var(--color-border)] flex flex-wrap items-center justify-between gap-3", children: [
            /* @__PURE__ */ jsxs("div", {
                  children: [
              /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "AI-Selected Gap Analysis Questions" }),
              /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "AI selects 50 relevant items from the full SQL bank (batched if the bank is large). Add more manually below." }),
              gapSaveState !== "idle" && /* @__PURE__ */ jsx("p", {
                    className: `text-[10px] mt-1.5 font-bold uppercase tracking-wider ${gapSaveState === "saving" ? "text-[color:var(--color-warn-fg)]" : gapSaveState === "saved" ? "text-[color:var(--color-ok-fg)]" : "text-[color:var(--color-danger-fg)]"}`,
                    children: gapSaveState === "saving" ? "Saving…" : gapSaveState === "saved" ? "Saved to project" : "Could not save - try again"
                  })
                  ]
                }),
            /* @__PURE__ */ jsxs("div", {
                  className: "flex flex-wrap items-center gap-2", children: [
              isLeadAdvisor && !leadUiLockedQuestions && /* @__PURE__ */ jsx("button", {
                    type: "button",
                    onClick: () => runAiGapSelection(),
                    disabled: leadUiLockedQuestions || aiSelecting || bankLoading || qbVersionsLoading || !!(bankError || qbVersionsError) || !bankQuestions.length,
                    className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] shadow-sm",
                    children: aiSelecting ? "Selecting…" : "Run AI selection (50)"
                  }),
              /* @__PURE__ */ jsxs("select", {
                    value: domainFilter,
                    onChange: (e) => setDomainFilter(e.target.value),
                    disabled: leadUiLockedQuestions,
                    className: "bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-1.5 text-xs font-bold text-[color:var(--color-text-hero)] outline-none disabled:opacity-50 disabled:cursor-not-allowed",
                    children: [
                /* @__PURE__ */ jsx("option", { value: "All", children: "All Domains" }),
                      domains.map((d) => /* @__PURE__ */ jsx("option", { value: d, children: d }, d))
                    ]
                  }),
              /* @__PURE__ */ jsx("input", {
                    value: qSearch,
                    onChange: (e) => setQSearch(e.target.value),
                    placeholder: "Search questions...",
                    disabled: leadUiLockedQuestions,
                    className: "bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-1.5 text-xs text-[color:var(--color-text-hero)] outline-none w-48 disabled:opacity-50 disabled:cursor-not-allowed",
                  })
                  ]
                })
                ]
              }),
          /* @__PURE__ */ jsx("div", {
                className: "overflow-x-auto max-h-[500px] overflow-y-auto", children: /* @__PURE__ */ jsxs("table", {
                  className: "w-full text-left", children: [
            /* @__PURE__ */ jsx("thead", {
                    className: "sticky top-0 z-10", children: /* @__PURE__ */ jsxs("tr", {
                      className: "bg-[color:var(--color-accent-fill)] text-[10px] font-black uppercase tracking-[0.15em] text-white/80", children: [
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 w-[40px]" }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 w-[60px]", children: "Q No." }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[400px]", children: "Question" }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 w-[140px]", children: "Domain" }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 w-[100px]", children: "Actions" })
                      ]
                    })
                  }),
            /* @__PURE__ */ jsx("tbody", {
                    className: "divide-y divide-[color:var(--color-border)]", children: selectedQuestions.length === 0 ? /* @__PURE__ */ jsx("tr", {
                      children: /* @__PURE__ */ jsx("td", {
                        colSpan: 5,
                        className: "px-4 py-12 text-center text-sm text-[color:var(--color-text-muted)] font-medium",
                        children: "No questions yet. Click “Run AI selection (50)” or add rows from the question bank."
                      })
                    }) : filteredSelectedQuestions.length === 0 ? /* @__PURE__ */ jsx("tr", {
                      children: /* @__PURE__ */ jsx("td", {
                        colSpan: 5,
                        className: "px-4 py-12 text-center text-sm text-[color:var(--color-text-muted)] font-medium",
                        children: "No questions match this domain or search. Try “All Domains” or clear the search box."
                      })
                    }) : filteredSelectedQuestions.map((q) => /* @__PURE__ */ jsxs("tr", {
                      className: "group hover:bg-[color:var(--color-bg-alt)] transition-all", children: [
              /* @__PURE__ */ jsx("td", {
                        className: "px-4 py-3",
                        children: /* @__PURE__ */ jsx("input", {
                          type: "checkbox",
                          checked: q.selected,
                          onChange: () => toggleQuestion(q.id),
                          disabled: leadUiLockedQuestions,
                          className: "rounded border-[color:var(--color-border-strong)] text-[color:var(--color-accent)] focus:ring-[color:var(--color-info-line)] disabled:opacity-50",
                        }),
                      }),
              /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-sm font-bold text-[color:var(--color-text-muted)]", children: q.qNo }),
              /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-sm text-[color:var(--color-text-hero)]", children: q.question }),
              /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("span", { className: "px-2 py-1 bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[9px] font-black uppercase", children: q.domain }) }),
              /* @__PURE__ */ jsx("td", {
                        className: "px-4 py-3", children: /* @__PURE__ */ jsxs("div", {
                          className: "flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity", children: [
                /* @__PURE__ */ jsx("button", {
                                  onClick: () => startEditQuestion(q),
                                  disabled: leadUiLockedQuestions,
                                  className: "p-1.5 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-accent)] transition-colors disabled:opacity-30 disabled:pointer-events-none",
                                  title: "Edit",
                                  children: /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" }) }),
                                }),
                /* @__PURE__ */ jsx("button", {
                                  onClick: () => removeQuestion(q.id),
                                  disabled: leadUiLockedQuestions,
                                  className: "p-1.5 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger-fg)] transition-colors disabled:opacity-30 disabled:pointer-events-none",
                                  title: "Delete",
                                  children: /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M6 18L18 6M6 6l12 12" }) }),
                                })
                          ]
                        })
                      })
                      ]
                    }, q.id))
                  })
                  ]
                })
              })
              ]
            }),
              editingQId && /* @__PURE__ */ jsx("div", {
                className: "fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4", onClick: () => setEditingQId(null), children: /* @__PURE__ */ jsxs("div", {
                  className: "bg-[color:var(--color-bg-white)] rounded-2xl shadow-2xl p-6 w-full max-w-xl space-y-4", onClick: (e) => e.stopPropagation(), children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Edit Question" }),
          /* @__PURE__ */ jsx("textarea", {
                    value: editQText,
                    onChange: (e) => setEditQText(e.target.value),
                    readOnly: leadUiLockedQuestions,
                    className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] min-h-[100px] read-only:opacity-60",
                  }),
          /* @__PURE__ */ jsxs("div", {
                    className: "flex items-center gap-2 justify-end", children: [
            /* @__PURE__ */ jsx("button", { onClick: () => setEditingQId(null), className: "px-4 py-2 text-xs font-bold text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-hero)] transition-colors", children: "Cancel" }),
            /* @__PURE__ */ jsx("button", {
                      onClick: saveEditQuestion,
                      disabled: leadUiLockedQuestions,
                      className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-lg text-xs font-black uppercase hover:opacity-90 transition-all disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] disabled:cursor-not-allowed",
                      children: "Save",
                    })
                    ]
                  })
                  ]
                })
              }),
              showAddQ ? /* @__PURE__ */ jsxs("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-2xl border shadow-sm p-6 space-y-3", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Add Custom Question" }),
          /* @__PURE__ */ jsx("textarea", {
                    value: newQText,
                    onChange: (e) => setNewQText(e.target.value),
                    placeholder: "Enter your question...",
                    readOnly: leadUiLockedQuestions,
                    className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] min-h-[80px] read-only:opacity-60",
                  }),
          /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center gap-3", children: [
            /* @__PURE__ */ jsx("select", {
                      value: newQDomain,
                      onChange: (e) => setNewQDomain(e.target.value),
                      disabled: leadUiLockedQuestions,
                      className: "bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-xs font-bold text-[color:var(--color-text-hero)] outline-none disabled:opacity-50",
                      children: domainOptionsForCustom.map((d) => /* @__PURE__ */ jsx("option", { value: d, children: d }, d)),
                    }),
            /* @__PURE__ */ jsxs("select", {
                      value: newQResponseType,
                      onChange: (e) => setNewQResponseType(e.target.value),
                      disabled: leadUiLockedQuestions,
                      "aria-label": "Response type",
                      className: "bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-xs font-bold text-[color:var(--color-text-hero)] outline-none disabled:opacity-50",
                      children: [
                        /* @__PURE__ */ jsx("option", { value: "", children: "Select response type…" }),
                        ...responseTypeOptions.map((t) => /* @__PURE__ */ jsx("option", { value: t, children: t }, t)),
                      ],
                    }),
            /* @__PURE__ */ jsx("div", { className: "flex-1" }),
            /* @__PURE__ */ jsx("button", { onClick: () => setShowAddQ(false), className: "px-4 py-2 text-xs font-bold text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-hero)]", children: "Cancel" }),
            /* @__PURE__ */ jsx("button", {
                      onClick: addCustomQuestion,
                      disabled: leadUiLockedQuestions || !newQText.trim() || !newQResponseType,
                      className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-lg text-xs font-black uppercase hover:opacity-90 transition-all disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] disabled:cursor-not-allowed",
                      children: "Add Question",
                    }),
                  ]
                })
                ]
              }) : /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => setShowAddQ(true),
                disabled: leadUiLockedQuestions,
                className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] disabled:cursor-not-allowed",
                children: "+ Add Custom Question",
              }),
        /* @__PURE__ */ jsxs("details", {
                className: "bg-[color:var(--color-bg-white)] rounded-2xl border shadow-sm group/qbank", children: [
          /* @__PURE__ */ jsxs("summary", {
                  className: "px-8 py-4 list-none flex flex-wrap items-center justify-between gap-3 cursor-pointer hover:bg-[color:var(--color-bg-alt)] transition-all [&::-webkit-details-marker]:hidden", children: [
              /* @__PURE__ */ jsxs("span", {
                      className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: [
                    "+ Add from Question Bank (",
                        availableFromBankCount,
                    " available)"
                      ]
                    }),
              /* @__PURE__ */ jsxs("div", {
                      className: "flex flex-wrap items-center gap-2 flex-1 justify-end min-w-0", children: [
              /* @__PURE__ */ jsxs("label", {
                      className: "flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-[color:var(--color-text-muted)] shrink-0", children: [
                  /* @__PURE__ */ jsx("span", { className: "whitespace-nowrap", children: "Version" }),
                  /* @__PURE__ */ jsxs("select", {
                          value: selectedQbVersionId || "",
                          onChange: (e) => setSelectedQbVersionId(e.target.value || null),
                          onClick: (e) => e.stopPropagation(),
                          onMouseDown: (e) => e.stopPropagation(),
                          onKeyDown: (e) => e.stopPropagation(),
                          disabled: leadUiLockedQuestions || qbVersionsLoading || qbVersions.length === 0,
                          "aria-label": "Question bank version",
                          className: "max-w-[14rem] bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-xs font-bold text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed", children: [
                    qbVersionsLoading && /* @__PURE__ */ jsx("option", { value: "", children: "Loading…" }),
                    !qbVersionsLoading && qbVersions.length === 0 && /* @__PURE__ */ jsx("option", { value: "", children: "No versions" }),
                    !qbVersionsLoading &&
                      qbVersions.map((v) =>
                        /* @__PURE__ */ jsx(
                          "option",
                          {
                            value: v.id,
                            children: `${v.label}${typeof v.totalQuestions === "number" ? ` (${v.totalQuestions})` : ""}`,
                          },
                          v.id,
                        ),
                      ),
                  ]
                        })
                      ]
                    }),
              /* @__PURE__ */ jsx("input", {
                      type: "search",
                      value: bankPanelSearch,
                      onChange: (e) => setBankPanelSearch(e.target.value),
                      onClick: (e) => e.stopPropagation(),
                      onMouseDown: (e) => e.stopPropagation(),
                      onKeyDown: (e) => e.stopPropagation(),
                      placeholder: "Search question bank…",
                      "aria-label": "Search question bank",
                      disabled: leadUiLockedQuestions,
                      className: "min-w-[12rem] flex-1 max-w-md bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-xs text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] cursor-text disabled:opacity-50"
                    })
                  ]
                })
                  ]
                }),
          /* @__PURE__ */ jsx("div", {
                  className: "px-8 py-4 border-t max-h-[min(480px,70vh)] overflow-y-auto", children: (() => {
                    const rows = filteredBankQuestions.filter((q) => !selectedIds.has(q.id));
                    return rows.length === 0 ? /* @__PURE__ */ jsx("p", {
                      className: "text-sm text-[color:var(--color-text-muted)] font-medium py-6 text-center",
                      children: bankPanelSearch.trim() ? "No bank questions match this search (or domain filter). Try different keywords." : "All matching bank questions are already in your list."
                    }) : rows.map((q) => /* @__PURE__ */ jsxs("div", {
                    className: "flex items-center justify-between gap-3 py-2 border-b border-[color:var(--color-border)] last:border-0", children: [
            /* @__PURE__ */ jsxs("div", {
                      className: "flex-1 min-w-0", children: [
              /* @__PURE__ */ jsxs("span", {
                        className: "text-xs font-bold text-[color:var(--color-text-muted)] mr-2 whitespace-nowrap", children: [
                          "Q",
                          q.qNo
                        ]
                      }),
              /* @__PURE__ */ jsx("span", {
                        className: "text-xs text-[color:var(--color-text-muted-dark)]", children: q.question.length > 140 ? `${q.question.substring(0, 140)}…` : q.question
                      })
                      ]
                    }),
            /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: () => addQuestionFromBank(q),
                      disabled: leadUiLockedQuestions,
                      className: "shrink-0 px-3 py-1 bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] rounded-lg text-[9px] font-black uppercase hover:bg-[color:var(--color-accent-tint)] transition-all disabled:opacity-50",
                      children: "Add",
                    })
                    ]
                  }, q.id));
                  })()
                })
                ]
              }),
        /* @__PURE__ */ jsxs("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
          /* @__PURE__ */ jsxs("div", {
                  className: "px-8 py-5 border-b border-[color:var(--color-border)] flex flex-wrap items-center justify-between gap-3", children: [
            /* @__PURE__ */ jsxs("div", {
                    className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Issues - Threats - Opportunities" }),
              /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "AI picks 10 per category; click a row to swap to another bank item or add manually." }),
              issueBankLoading && /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1", children: "Loading issue bank\u2026" }),
              issueBankError && /* @__PURE__ */ jsx("div", { className: "text-[10px] text-[color:var(--color-danger-fg)] mt-2 font-bold", children: issueBankError }),
              aiIssuesError && /* @__PURE__ */ jsx("div", { className: "text-[10px] text-[color:var(--color-warn-fg)] mt-2 font-bold", children: aiIssuesError })
                    ]
                  }),
            isLeadAdvisor && !leadUiLockedQuestions && /* @__PURE__ */ jsx("button", {
                    type: "button",
                    onClick: () => runAiIssueSelection(),
                    disabled: leadUiLockedQuestions || aiIssuesSelecting || issueBankLoading || !!issueBankError || issueBankItems.length === 0,
                    className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] shadow-sm shrink-0",
                    children: aiIssuesSelecting ? "Selecting\u2026" : "Run AI selection (10 \xD7 4)"
                  })
                  ]
                }),
          /* @__PURE__ */ jsx("div", {
                  className: "p-6 space-y-6", children: ISSUE_CATEGORIES.map((cat) => {
                    const catRows = selectedIssueRows[cat] || [];
                    const usedIds = new Set(catRows.map((r) => r.bankItemId).filter(Boolean));
                    const availableForCat = issueBankItems.filter((i) => i.category === cat && !usedIds.has(i.id));
                    return /* @__PURE__ */ jsxs("div", {
                      className: "space-y-3", children: [
              /* @__PURE__ */ jsx("div", {
                        className: "flex items-center justify-between", children: /* @__PURE__ */ jsxs("h5", {
                          className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: [
                            cat,
                            " ",
                /* @__PURE__ */ jsxs("span", {
                              className: "text-[color:var(--color-text-muted)] font-bold", children: [
                                "(",
                                catRows.length,
                                "/10)"
                              ]
                            })
                          ]
                        })
                      }),
              /* @__PURE__ */ jsx("div", {
                        className: "space-y-1.5", children: catRows.map((row, idx) => {
                          const otherUsed = new Set(catRows.map((r, j) => j !== idx ? r.bankItemId : null).filter(Boolean));
                          const swapOptions = issueBankItems.filter((i) => i.category === cat && (!otherUsed.has(i.id) || i.id === row.bankItemId));
                          return /* @__PURE__ */ jsxs("div", {
                          className: "flex items-center gap-2 group bg-[color:var(--color-surface-sunken)] rounded-xl px-4 py-2.5 border border-[color:var(--color-border)]", children: [
                /* @__PURE__ */ jsx("span", { className: "w-5 h-5 rounded-md bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] flex items-center justify-center text-[9px] font-black shrink-0", children: idx + 1 }),
                            editingIssue?.cat === cat && editingIssue?.idx === idx ? /* @__PURE__ */ jsxs(
                              "select",
                              {
                                  value: row.bankItemId || "",
                                  disabled: leadUiLockedQuestions,
                                onChange: (e) => {
                                    const id = e.target.value;
                                    if (!id) return;
                                    const bi = issueBankItems.find((x) => x.id === id);
                                    if (!bi) return;
                                    setSelectedIssueRows((prev) => {
                                    const updated = [...prev[cat] || []];
                                      updated[idx] = { bankItemId: bi.id, issue: bi.issue };
                                    return { ...prev, [cat]: updated };
                                  });
                                  setEditingIssue(null);
                                },
                                autoFocus: true,
                                onBlur: () => setEditingIssue(null),
                                className: "flex-1 bg-[color:var(--color-bg-white)] border border-[color:var(--color-info-line)] rounded-lg px-3 py-1.5 text-xs font-bold text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]",
                                children: [
                                    !row.bankItemId && /* @__PURE__ */ jsx("option", { value: "", children: row.issue }),
                                    swapOptions.map((i) => /* @__PURE__ */ jsx("option", { value: i.id, children: i.issue }, i.id))
                                  ]
                                }
                              ) : /* @__PURE__ */ jsx("button", {
                                type: "button",
                                className: "flex-1 text-left text-xs text-[color:var(--color-text-hero)] font-medium hover:text-[color:var(--color-accent)] transition-colors disabled:opacity-50",
                                disabled: leadUiLockedQuestions,
                                onClick: () => setEditingIssue({ cat, idx }),
                                children: row.issue
                              }),
                /* @__PURE__ */ jsxs("div", {
                              className: "flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0", children: [
                  /* @__PURE__ */ jsx("button", {
                                    type: "button",
                                    disabled: leadUiLockedQuestions,
                                    onClick: () => setEditingIssue({ cat, idx }),
                                    className: "p-1 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-accent)] transition-colors disabled:opacity-30",
                                    title: "Change item",
                                    children: /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" }) }),
                                  }),
                  /* @__PURE__ */ jsx("button", {
                                    type: "button",
                                    disabled: leadUiLockedQuestions,
                                onClick: () => {
                                      setSelectedIssueRows((prev) => {
                                    const updated = [...prev[cat] || []];
                                    updated.splice(idx, 1);
                                    return { ...prev, [cat]: updated };
                                  });
                                    },
                                    className: "p-1 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger-fg)] transition-colors disabled:opacity-30",
                                    title: "Delete",
                                    children: /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M6 18L18 6M6 6l12 12" }) }),
                              })
                              ]
                            })
                          ]
                          }, idx);
                        })
                      }),
                        catRows.length < 10 && availableForCat.length > 0 && /* @__PURE__ */ jsx("div", {
                          className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs(
                            "select",
                            {
                              value: "",
                              disabled: leadUiLockedQuestions,
                              onChange: (e) => {
                                const id = e.target.value;
                                if (!id) return;
                                const bi = issueBankItems.find((x) => x.id === id);
                                if (!bi) return;
                                setSelectedIssueRows((prev) => ({ ...prev, [cat]: [...prev[cat] || [], { bankItemId: bi.id, issue: bi.issue }] }));
                              },
                              className: "flex-1 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-xs font-bold text-[color:var(--color-text-muted)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] disabled:opacity-50",
                              children: [
                    /* @__PURE__ */ jsxs("option", {
                                value: "", children: [
                                  "+ Add from ",
                                  cat,
                                  "..."
                                ]
                              }),
                                availableForCat.map((ai) => /* @__PURE__ */ jsx("option", { value: ai.id, children: ai.issue }, ai.id))
                              ]
                            }
                          )
                        })
                      ]
                    }, cat);
                  })
                })
                ]
              }),
          isLeadAdvisor && step3Status === "Draft" && !leadUiLockedQuestions && /* @__PURE__ */ jsx("div", {
                className: "flex justify-end pt-2",
                children: /* @__PURE__ */ jsx("button", {
                  type: "button",
                  disabled: leadUiLockedQuestions || questionsStepSubmitting,
                  onClick: () => submitQuestionsAndContinueToRiskScale(),
                  className: "px-6 py-3 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] disabled:cursor-not-allowed",
                  children: questionsStepSubmitting ? "Saving\u2026" : "Submit & continue to Risk Scale",
                }),
              }),
            ]
          }),
          leadSubStep === "risk-scale" && /* @__PURE__ */ jsxs("div", {
            className: "space-y-6", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl p-8", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)] mb-4", children: "Select Risk Rating Scale" }),
          /* @__PURE__ */ jsx("div", {
                className: "grid grid-cols-3 gap-4", children: ["3", "5", "10"].map((scale) => /* @__PURE__ */ jsxs(
                  "button",
                  {
                    onClick: () => setRiskScale(scale),
                    disabled: leadUiLockedRiskScale,
                    className: `p-6 rounded-2xl border-2 transition-all text-center disabled:opacity-50 disabled:cursor-not-allowed ${riskScale === scale ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent-tint)] shadow-lg" : "border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] hover:border-[color:var(--color-border-strong)]"}`,
                    children: [
                /* @__PURE__ */ jsx("div", { className: "text-4xl font-black text-[color:var(--color-text-hero)]", children: scale }),
                /* @__PURE__ */ jsxs("div", {
                      className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mt-2", children: [
                        scale,
                        "-Point Scale"
                      ]
                    }),
                /* @__PURE__ */ jsx("div", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1", children: scale === "3" ? "Minor / Moderate / High" : scale === "5" ? "Negligible \u2192 Very High" : "Full 1-10 range" })
                    ]
                  },
                  scale
                ))
              })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
          /* @__PURE__ */ jsx("div", {
                className: "px-8 py-5 border-b border-[color:var(--color-border)]", children: /* @__PURE__ */ jsxs("h4", {
                  className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: [
                    "Severity Ranks - ",
                    riskScale,
                    "-Point Scale"
                  ]
                })
              }),
          /* @__PURE__ */ jsx("div", {
                className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", {
                  className: "w-full text-left text-xs min-w-[800px]", children: [
            /* @__PURE__ */ jsx("thead", {
                    children: /* @__PURE__ */ jsxs("tr", {
                      className: "bg-[color:var(--color-accent-fill)] text-[7px] sm:text-[8px] font-black uppercase tracking-wider text-white/80", children: [
              /* @__PURE__ */ jsx("th", { className: "px-2 py-3 sticky left-0 bg-[color:var(--color-accent-fill)] z-[1] min-w-[7rem]", children: "Category" }),
                        ...SEVERITY_COLUMNS.map((col) => /* @__PURE__ */ jsx("th", { className: "px-2 py-3 whitespace-nowrap", children: col }, col))
                      ]
                    })
                  }),
            /* @__PURE__ */ jsx("tbody", {
                    className: "divide-y divide-[color:var(--color-border)]", children: SEVERITY_CATEGORIES.map((cat, i) => /* @__PURE__ */ jsxs("tr", {
                      className: "hover:bg-[color:var(--color-bg-alt)]", children: [
              /* @__PURE__ */ jsx("td", { className: "px-2 py-3 font-bold text-[color:var(--color-text-hero)] sticky left-0 bg-[color:var(--color-bg-white)] z-[1] shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]", children: cat }),
                        ...SEVERITY_COLUMNS.map((col) => {
                          const v = severityCellValue(riskScale, col);
                          return /* @__PURE__ */ jsx("td", {
                            key: col,
                            className: "px-2 py-2 text-center align-middle",
                            children: v === "NA" ? /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-text-muted)] font-medium", children: "NA" }) : /* @__PURE__ */ jsx("span", { className: severityCellPillClass(v), children: v })
                          }, col);
                        })
                      ]
                    }, i))
                  })
                  ]
                })
              })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-2xl border p-6 shadow-sm", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)] mb-3", children: "Risk Calculation Logic" }),
          /* @__PURE__ */ jsxs("div", {
                className: "flex items-center gap-2 text-sm font-bold text-[color:var(--color-text-hero)] bg-[color:var(--color-surface-sunken)] rounded-xl p-4", children: [
            /* @__PURE__ */ jsx("span", { className: "px-3 py-1 bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] rounded-lg", children: "Severity" }),
                  " \xD7 ",
            /* @__PURE__ */ jsx("span", { className: "px-3 py-1 bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] rounded-lg", children: "Likelihood" }),
                  " \xD7 ",
            /* @__PURE__ */ jsx("span", { className: "px-3 py-1 bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] rounded-lg", children: "Consequence" }),
                  " \xD7 ",
            /* @__PURE__ */ jsx("span", { className: "px-3 py-1 bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] rounded-lg", children: "Control Strength" })
                ]
              }),
          /* @__PURE__ */ jsxs("p", {
                className: "text-xs text-[color:var(--color-text-muted)] mt-3", children: [
                  "Significance threshold for ",
                  riskScale,
                  "-point: ",
            /* @__PURE__ */ jsx("strong", { className: "text-[color:var(--color-text-hero)]", children: String(getSignificanceThreshold(riskScale)) })
                ]
              })
              ]
            }),
          isLeadAdvisor && step3Status === "Draft" && !leadUiLockedRiskScale && /* @__PURE__ */ jsx("div", {
                className: "flex justify-end pt-2",
                children: /* @__PURE__ */ jsx("button", {
                  type: "button",
                  disabled: leadUiLockedRiskScale || riskScaleStepSubmitting,
                  onClick: () => submitRiskScaleAndNotifyAdvisor(),
                  className: "px-6 py-3 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] disabled:cursor-not-allowed",
                  children: riskScaleStepSubmitting ? "Saving\u2026" : "Submit & notify advisor (Artefact steps)",
                }),
              }),
            ]
          }),
          leadSubStep === "artefact-class" && /* @__PURE__ */ jsxs("div", {
            className: "space-y-4",
            children: [
              (advisorArtefactsStatus === "awaiting_advisor" || advisorArtefactsStatus === "returned") && !advisorImpersonationActive && !advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
                className: "rounded-2xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-6 py-4 text-sm text-[color:var(--color-warn-fg)] font-medium flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
                children: [
                  /* @__PURE__ */ jsx("span", {
                    children: advisorArtefactsStatus === "returned" ? "The advisor is updating artefact classification and templates after your return for revision. You will see their work here when they submit again for review." : "Pending from advisor: artefact classification and templates are filled on the advisor dashboard. You will review them here once they submit for your approval.",
                  }),
                  isLeadAdvisor && /* @__PURE__ */ jsx("button", {
                    type: "button",
                    onClick: () => setAdvisorActOnBehalfOpen(true),
                    className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)] underline underline-offset-2 hover:text-[color:var(--color-warn-fg)]",
                    children: "Act on behalf of Advisor →",
                  }),
                ],
              }),
              (advisorArtefactsStatus === "awaiting_advisor" || advisorArtefactsStatus === "returned") && isLeadAdvisor && advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
                className: "rounded-2xl border border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] px-6 py-4 text-sm text-[color:var(--color-danger-fg)] font-medium flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
                children: [
                  /* @__PURE__ */ jsx("span", {
                    children: "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
                  }),
                  /* @__PURE__ */ jsx("button", {
                    type: "button",
                    onClick: () => setAdvisorActOnBehalfOpen(true),
                    className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] underline underline-offset-2 hover:text-[color:var(--color-danger-fg)]",
                    children: "Start a new session →",
                  }),
                ],
              }),
              isLeadAdvisor && advisorImpersonationActive && (advisorArtefactsStatus === "awaiting_advisor" || advisorArtefactsStatus === "returned") && /* @__PURE__ */ jsxs("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-info-line)] shadow-2xl overflow-hidden",
                children: [
                  /* @__PURE__ */ jsxs("div", {
                    className: "px-8 py-5 border-b border-[color:var(--color-border)] bg-[color:var(--color-accent-tint)]",
                    children: [
                      /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Artefacts Index - acting on behalf of Advisor" }),
                      /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "This save is attributed to your active impersonation session and audited. Import catalogue templates or classify rows below, then submit for your own review." }),
                    ],
                  }),
                  /* @__PURE__ */ jsx("div", {
                    className: "px-8 py-4 border-b border-[color:var(--color-border)]",
                    children: /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: () => void syncArtefactRowsFromPolicyHub(),
                      disabled: syncingArtefactsFromPolicyHub,
                      className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-hero)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-bg-alt)] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
                      children: syncingArtefactsFromPolicyHub ? "Importing…" : "Import all catalogue templates",
                    }),
                  }),
                  /* @__PURE__ */ jsx("div", {
                    className: "overflow-x-auto",
                    children: /* @__PURE__ */ jsxs("table", {
                      className: "w-full text-left",
                      children: [
                        /* @__PURE__ */ jsx("thead", {
                          children: /* @__PURE__ */ jsxs("tr", {
                            className: "bg-[color:var(--color-accent-fill)] text-[10px] font-black uppercase tracking-[0.15em] text-white/80",
                            children: [
                              /* @__PURE__ */ jsx("th", { className: "px-6 py-4 w-[60px]", children: "#" }),
                              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[250px]", children: "Artefact Name" }),
                              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[280px]", children: "Classification" }),
                              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 w-[80px]", children: "Actions" }),
                            ],
                          }),
                        }),
                        /* @__PURE__ */ jsxs("tbody", {
                          className: "divide-y divide-[color:var(--color-border)]",
                          children: [
                            artefactIndex.length === 0 && /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { colSpan: 4, className: "px-6 py-10 text-center text-sm text-[color:var(--color-text-muted)]", children: "No artefacts yet. Click Import all catalogue templates to load them from blob storage." }) }),
                            artefactIndex.map((a, i) => /* @__PURE__ */ jsxs("tr", {
                              className: "group hover:bg-[color:var(--color-bg-alt)] transition-all",
                              children: [
                                /* @__PURE__ */ jsx("td", { className: "px-6 py-3 text-sm font-bold text-[color:var(--color-text-muted)]", children: i + 1 }),
                                /* @__PURE__ */ jsx("td", {
                                  className: "px-4 py-3",
                                  children: /* @__PURE__ */ jsx("input", {
                                    value: a.name,
                                    onChange: (e) => updateArtefactRow(a.id, "name", e.target.value),
                                    placeholder: "Enter artefact name...",
                                    className: "w-full bg-transparent border-b border-transparent hover:border-[color:var(--color-border)] focus:border-[color:var(--color-accent)] outline-none text-sm text-[color:var(--color-text-hero)] py-1 transition-colors",
                                  }),
                                }),
                                /* @__PURE__ */ jsx("td", {
                                  className: "px-4 py-3",
                                  children: /* @__PURE__ */ jsx("select", {
                                    value: a.classification,
                                    onChange: (e) => updateArtefactRow(a.id, "classification", e.target.value),
                                    className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-1.5 text-xs font-bold text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]",
                                    children: docClassRows.map((d) => /* @__PURE__ */ jsx("option", { value: d.classificationType, children: d.classificationType }, d.id)),
                                  }),
                                }),
                                /* @__PURE__ */ jsx("td", {
                                  className: "px-4 py-3",
                                  children: /* @__PURE__ */ jsx("button", {
                                    type: "button",
                                    onClick: () => deleteArtefactRow(a.id),
                                    className: "p-1.5 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger-fg)] transition-colors",
                                    title: "Delete",
                                    children: "✕",
                                  }),
                                }),
                              ],
                            }, a.id)),
                          ],
                        }),
                      ],
                    }),
                  }),
                  /* @__PURE__ */ jsxs("div", {
                    className: "px-8 py-4 border-t border-[color:var(--color-border)] flex flex-wrap justify-end gap-2",
                    children: [
                      /* @__PURE__ */ jsx("button", {
                        type: "button",
                        onClick: () => void saveAdvisorArtefactClassificationImpersonated(),
                        disabled: advisorArtefactsSaveBusy,
                        className: "px-5 py-2.5 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-hero)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-bg-alt)] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
                        children: advisorArtefactsSaveBusy ? "Saving…" : "Save (as Advisor)",
                      }),
                      artefactIndex.length > 0 && /* @__PURE__ */ jsx("button", {
                        type: "button",
                        onClick: () => void submitAdvisorArtefactsForLdReviewImpersonated(),
                        disabled: advisorArtefactsSaveBusy,
                        className: "px-5 py-2.5 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] disabled:cursor-not-allowed",
                        children: "Submit for LD review (as Advisor)",
                      }),
                    ],
                  }),
                ],
              }),
              isLeadAdvisor && /* @__PURE__ */ jsx(ImpersonationDialog, {
                open: advisorActOnBehalfOpen,
                onClose: () => setAdvisorActOnBehalfOpen(false),
                projectId: project?.id,
                projectName: project?.companyName || project?.name,
                members: advisorMembersForActOnBehalf,
                initialEffectiveRole: "advisor",
                initialReason: "Blocked on: Advisor to classify and submit the artefact index (Project Onboarding). Select the specific Advisor below - the server requires a named Advisor, not a role-only session, before it will accept this write.",
              }),
              (advisorArtefactsStatus === "submitted" || advisorArtefactsStatus === "approved") && /* @__PURE__ */ jsxs("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden",
                children: [
                  /* @__PURE__ */ jsxs("div", {
                    className: "px-8 py-5 border-b border-[color:var(--color-border)]",
                    children: [
                      /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Artefacts Index" }),
                      /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: advisorArtefactsStatus === "approved" ? "Approved - read-only snapshot." : "Submitted by advisor - read-only on this Artefacts Index step. Review classification criteria and sample docs, then the index below - approve or return." }),
                    ],
                  }),
                  /* @__PURE__ */ jsxs("div", {
                    className: "border-b border-[color:var(--color-border)] px-8 py-6 space-y-4 bg-[color:var(--color-surface-sunken)]",
                    children: [
                      /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Artefact index classification" }),
                      /* @__PURE__ */ jsx("h5", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Document classification criteria" }),
                      /* @__PURE__ */ jsx("p", {
                        className: "text-[10px] text-[color:var(--color-text-muted)] font-medium max-w-3xl",
                        children: "Reference for how artefact templates are classified. Same criteria the advisor sees when building the pack.",
                      }),
                      /* @__PURE__ */ jsx("div", {
                        className: "overflow-x-auto",
                        children: /* @__PURE__ */ jsxs("table", {
                          className: "w-full text-left",
                          children: [
                            /* @__PURE__ */ jsx("thead", {
                              children: /* @__PURE__ */ jsxs("tr", {
                                className: "bg-[color:var(--color-accent-fill)] text-[10px] font-black uppercase tracking-[0.15em] text-white/80",
                                children: [
                                  /* @__PURE__ */ jsx("th", { className: "px-6 py-4 w-[60px]", children: "#" }),
                                  /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[220px]", children: "Classification type" }),
                                  /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[240px]", children: "Distribution protocol" }),
                                  /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[120px]", children: "Badge" }),
                                ],
                              }),
                            }),
                            /* @__PURE__ */ jsx("tbody", {
                              className: "divide-y divide-[color:var(--color-border)] bg-[color:var(--color-bg-white)]",
                              children: docClassRows.map((dc, idx) =>
                                /* @__PURE__ */ jsxs(
                                  "tr",
                                  {
                                    className: "hover:bg-[color:var(--color-bg-alt)] transition-all",
                                    children: [
                                      /* @__PURE__ */ jsx("td", { className: "px-6 py-3 text-sm font-bold text-[color:var(--color-text-muted)]", children: idx + 1 }),
                                      /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-sm text-[color:var(--color-text-hero)] font-medium", children: dc.classificationType || "-" }),
                                      /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-xs text-[color:var(--color-text-muted-dark)]", children: dc.distributionProtocol || "-" }),
                                      /* @__PURE__ */ jsx("td", {
                                        className: "px-4 py-3",
                                        children: /* @__PURE__ */ jsx("span", {
                                          className: `inline-flex px-2 py-0.5 rounded text-[8px] font-black uppercase border ${classificationColors[dc.classificationType] || "bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted-dark)] border-[color:var(--color-border)]"}`,
                                          children: classificationBadgeShortLabel[dc.classificationType] || "-",
                                        }),
                                      }),
                                    ],
                                  },
                                  dc.id,
                                ),
                              ),
                            }),
                          ],
                        }),
                      }),
                    ],
                  }),
                  /* @__PURE__ */ jsxs("div", {
                    className: "border-b border-[color:var(--color-border)] px-8 py-6 space-y-3 bg-[color:var(--color-bg-white)]",
                    children: [
                      /* @__PURE__ */ jsxs("div", {
                        className: "space-y-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-5 py-4",
                        children: [
                          /* @__PURE__ */ jsx("h5", {
                            className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-hero)]",
                            children: "Sample Docs",
                          }),
                          /* @__PURE__ */ jsx("p", {
                            className: "text-[10px] text-[color:var(--color-text-muted)] font-medium",
                            children: "Educator reference templates. Preview opens in a popup.",
                          }),
                          ONBOARDING_SAMPLE_ARTEFACT_ROWS.map((sampleRow) =>
                            /* @__PURE__ */ jsxs(
                              "div",
                              {
                                className: "flex flex-wrap items-center gap-4 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-4 py-3",
                                children: [
                                  /* @__PURE__ */ jsxs("div", {
                                    className: "min-w-0 flex-1 space-y-0.5",
                                    children: [
                                      /* @__PURE__ */ jsx("p", {
                                        className: "text-[10px] font-medium text-[color:var(--color-text-muted)] break-all",
                                        children: stripTrailingDocumentExtension(sampleRow.fileNameWithExt),
                                      }),
                                      /* @__PURE__ */ jsx("p", {
                                        className: "text-sm font-bold text-[color:var(--color-text-hero)] line-clamp-2",
                                        children: sampleRow.title,
                                      }),
                                    ],
                                  }),
                                  /* @__PURE__ */ jsx("span", {
                                    className:
                                      "inline-flex shrink-0 px-2 py-0.5 rounded text-[8px] font-black uppercase border bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]",
                                    children: "Public",
                                  }),
                                  /* @__PURE__ */ jsx("button", {
                                    type: "button",
                                    onClick: () => setLeadOnboardingSamplePreview(sampleRow),
                                    className:
                                      "shrink-0 px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg",
                                    children: "Preview",
                                  }),
                                ],
                              },
                              sampleRow.id,
                            ),
                          ),
                        ],
                      }),
                    ],
                  }),
                  /* @__PURE__ */ jsx("div", {
                    className: "overflow-x-auto",
                    children: /* @__PURE__ */ jsxs("table", {
                      className: "w-full text-left",
                      children: [
                        /* @__PURE__ */ jsx("thead", {
                          children: /* @__PURE__ */ jsxs("tr", {
                            className: "bg-[color:var(--color-accent-fill)] text-[10px] font-black uppercase tracking-[0.15em] text-white/80",
                            children: [
                              /* @__PURE__ */ jsx("th", { className: "px-6 py-4 w-[60px]", children: "#" }),
                              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[250px]", children: "Artefact Name" }),
                              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[150px]", children: "Template file" }),
                              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[200px]", children: "Classification" }),
                            ],
                          }),
                        }),
                        /* @__PURE__ */ jsx("tbody", {
                          className: "divide-y divide-[color:var(--color-border)]",
                          children: ldArtefactRowsForLd.map((a, i) => /* @__PURE__ */ jsxs("tr", {
                            className: "hover:bg-[color:var(--color-bg-alt)] transition-all",
                            children: [
                              /* @__PURE__ */ jsx("td", { className: "px-6 py-3 text-sm font-bold text-[color:var(--color-text-muted)]", children: i + 1 }),
                              /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-sm text-[color:var(--color-text-hero)]", children: a.name || "-" }),
                              /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-sm text-[color:var(--color-text-muted-dark)]", children: artefactFileNameUiDisplay(a) || "-" }),
                              /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("span", { className: `px-2 py-0.5 rounded text-[8px] font-black uppercase border ${classificationColors[a.classification] || "bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted-dark)] border-[color:var(--color-border)]"}`, children: (a.classification || "Unclassified").split(" - ")[0] }) }),
                            ],
                          }, a.id)),
                        }),
                      ],
                    }),
                  }),
                  advisorArtefactsStatus === "submitted" && /* @__PURE__ */ jsx("div", {
                    className: "border-t border-[color:var(--color-border)] px-8 py-6 flex flex-wrap gap-3",
                    children: [
                      /* @__PURE__ */ jsx("button", {
                        type: "button",
                        onClick: () => ldReturnAdvisorArtefacts(),
                        disabled: onboardingLocked,
                        className: "px-6 py-3 bg-[color:var(--color-bg-white)] border border-[color:var(--color-danger-line)] text-[color:var(--color-danger-fg)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-danger-bg)] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
                        children: "Return for revision",
                      }),
                      /* @__PURE__ */ jsx("button", {
                        type: "button",
                        onClick: () => ldApproveAdvisorArtefacts(),
                        disabled: onboardingLocked,
                        className: "px-6 py-3 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] disabled:cursor-not-allowed",
                        children: "Approve & continue to Kick-off",
                      }),
                    ],
                  }),
                  /* @__PURE__ */ jsx(OnboardingSampleDocxPreviewModal, {
                    open: leadOnboardingSamplePreview != null,
                    sample: leadOnboardingSamplePreview,
                    onClose: () => setLeadOnboardingSamplePreview(null),
                    getApiToken,
                  }),
                ],
              }),
            ],
          }),
          leadSubStep === "kickoff" && /* @__PURE__ */ jsxs("div", {
            className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl p-8 space-y-6", children: [
        /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Gap Analysis Kick-off Meeting" }),
              advisorArtefactsStatus !== "approved" && /* @__PURE__ */ jsx("p", { className: "text-sm text-[color:var(--color-warn-fg)] bg-[color:var(--color-warn-bg)] border border-[color:var(--color-warn-line)] rounded-xl p-4", children: "Approve the advisor's artefact pack on the Artefacts Index step first, then schedule the kick-off here." }),
              advisorArtefactsStatus === "approved" && /* @__PURE__ */ jsxs("div", {
                className: "space-y-6", children: [
          /* @__PURE__ */ jsxs("div", {
                    className: "rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-5 space-y-4", children: [
              /* @__PURE__ */ jsxs("div", {
                      children: [
                /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Schedule (Google Calendar)" }),
                /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted-dark)] mt-1", children: "Pick date and time for the kick-off. Use the button to add the same slot to your Google Calendar (opens in a new tab). Project members are pre-filled as guests so you can send the invite after saving the event." }),
                      ]
                    }),
              /* @__PURE__ */ jsxs("div", {
                      className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [
                /* @__PURE__ */ jsxs("div", {
                        children: [
                  /* @__PURE__ */ jsx("label", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2 block", children: "Meeting start" }),
                  /* @__PURE__ */ jsx("input", {
                          type: "datetime-local",
                          value: kickoffStartLocal,
                          onChange: (e) => setKickoffStartLocal(e.target.value),
                          disabled: leadUiLockedKickoff,
                          className: "w-full bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] disabled:opacity-50",
                        }),
                        ]
                      }),
                /* @__PURE__ */ jsxs("div", {
                        children: [
                  /* @__PURE__ */ jsx("label", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2 block", children: "Status" }),
                  /* @__PURE__ */ jsx("div", { className: `w-full px-4 py-3 rounded-xl text-sm font-bold ${kickoffScheduled ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)]" : "bg-[color:var(--color-bg-white)] text-[color:var(--color-text-muted)] border border-[color:var(--color-border)]"}`, children: kickoffScheduled ? "\u2713 Scheduled" : "Not Scheduled" }),
                        ]
                      }),
                      ]
                    }),
              googleCalendarKickoffUrl && /* @__PURE__ */ jsx("div", {
                      children: /* @__PURE__ */ jsx(
                        "a",
                        {
                          href: googleCalendarKickoffUrl,
                          target: "_blank",
                          rel: "noreferrer",
                          className: "inline-flex items-center gap-2 px-4 py-2.5 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] rounded-xl text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] hover:bg-[color:var(--color-bg-alt)] transition-all shadow-sm",
                          children: [
                            /* @__PURE__ */ jsxs("svg", { className: "w-4 h-4 text-[color:var(--color-accent)]", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": true, children: [
                              /* @__PURE__ */ jsx("path", { d: "M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" }),
                            ] }),
                            "Add to Google Calendar",
                          ],
                        }
                      ),
                    }),
                  ]
                }),
          /* @__PURE__ */ jsxs("div", {
                  children: [
            /* @__PURE__ */ jsx("label", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2 block", children: "Agenda / Notes" }),
            /* @__PURE__ */ jsx("textarea", {
                    value: kickoffNotes,
                    onChange: (e) => setKickoffNotes(e.target.value),
                    placeholder: "Add agenda items, participants, logistics...",
                    readOnly: leadUiLockedKickoff,
                    className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] min-h-[120px] read-only:opacity-60",
                  }),
                  ]
                }),
          /* @__PURE__ */ jsxs("div", {
                  className: "rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-5 space-y-3", children: [
            /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Meeting transcriptions" }),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted-dark)]", children: "Upload transcripts or notes (.txt, .pdf, .docx, etc.). Files are stored securely; links are saved on this project." }),
            /* @__PURE__ */ jsx("input", {
              type: "file",
              className: "block w-full text-xs text-[color:var(--color-text-muted-dark)] file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:bg-[color:var(--color-accent-fill)] file:text-white hover:file:opacity-90 file:cursor-pointer",
              disabled: leadUiLockedKickoff || kickoffUploadBusy,
              onChange: (e) => uploadKickoffTranscriptFile(e),
            }),
            kickoffUploadBusy && /* @__PURE__ */ jsx("p", { className: "text-[10px] font-bold text-[color:var(--color-warn-fg)]", children: "Uploading\u2026" }),
            kickoffTranscripts.length > 0 && /* @__PURE__ */ jsx("ul", {
              className: "space-y-2 border-t border-[color:var(--color-border)] pt-3 mt-1",
              children: kickoffTranscripts.map((t) => /* @__PURE__ */ jsxs("li", {
                className: "flex items-center justify-between gap-3 text-sm",
                children: [
                  /* A pre-fix persisted entry (or a future no-op signer response) can carry a
                     null/missing url — never render that as a live-looking link (see lesson
                     audit-log-unconditional-success-before-persisted-check). */
                  t.url
                    ? /* @__PURE__ */ jsx("a", { href: t.url, target: "_blank", rel: "noreferrer", className: "text-[color:var(--color-accent)] font-semibold truncate hover:underline", title: t.fileName, children: t.fileName })
                    : /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-text-muted)] font-semibold truncate cursor-not-allowed", title: "Link unavailable - please remove and re-upload this file.", children: `${t.fileName} (link unavailable)` }),
                  /* @__PURE__ */ jsx("button", {
                    type: "button",
                    disabled: leadUiLockedKickoff,
                    onClick: () => removeKickoffTranscript(t.id),
                    className: "shrink-0 text-[10px] font-black uppercase text-[color:var(--color-danger-fg)] hover:text-[color:var(--color-danger-fg)] disabled:opacity-40",
                    children: "Remove",
                  }),
                ],
              }, t.id)),
            }),
                  ]
                }),
          /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    onClick: () => persistKickoffWizard(),
                    disabled: leadUiLockedKickoff || !kickoffStartLocal || kickoffScheduled || kickoffScheduleBusy,
                    className: `px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${kickoffScheduled ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)]" : "bg-[color:var(--color-accent-fill)] text-[color:var(--color-ok-fg)] hover:opacity-90"}`,
                    children: kickoffScheduled ? "\u2713 Meeting Scheduled" : kickoffScheduleBusy ? "Scheduling\u2026" : "Schedule Kick-off",
                  }
                ),
                ]
              }),
            ]
          }),
          leadSubStep === "review" && /* @__PURE__ */ jsx("div", {
            className: "space-y-4", children: /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl p-8 space-y-4", children: [
        /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Review Summary" }),
        /* @__PURE__ */ jsxs("div", {
                className: "grid grid-cols-2 gap-4", children: [
          /* @__PURE__ */ jsxs("div", {
                  className: "bg-[color:var(--color-surface-sunken)] rounded-xl p-4 border", children: [
            /* @__PURE__ */ jsx("div", { className: "text-xs font-bold text-[color:var(--color-text-muted)]", children: "Questions Selected" }),
            /* @__PURE__ */ jsx("div", { className: "text-2xl font-black mt-1", children: selectedQuestions.filter((q) => q.selected).length })
                  ]
                }),
          /* @__PURE__ */ jsxs("div", {
                  className: "bg-[color:var(--color-surface-sunken)] rounded-xl p-4 border", children: [
            /* @__PURE__ */ jsx("div", { className: "text-xs font-bold text-[color:var(--color-text-muted)]", children: "Risk Scale" }),
            /* @__PURE__ */ jsxs("div", {
                    className: "text-2xl font-black mt-1", children: [
                      riskScale,
                      "-Point"
                    ]
                  })
                  ]
                }),
          /* @__PURE__ */ jsxs("div", {
                  className: "bg-[color:var(--color-surface-sunken)] rounded-xl p-4 border", children: [
            /* @__PURE__ */ jsx("div", { className: "text-xs font-bold text-[color:var(--color-text-muted)]", children: "Significance threshold" }),
            /* @__PURE__ */ jsx("div", { className: "text-2xl font-black mt-1", children: String(getSignificanceThreshold(riskScale)) }),
            /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Product benchmark for significant risk (see Risk Scale step)." })
                  ]
                }),
          /* @__PURE__ */ jsxs("div", {
                  className: "bg-[color:var(--color-surface-sunken)] rounded-xl p-4 border", children: [
            /* @__PURE__ */ jsx("div", { className: "text-xs font-bold text-[color:var(--color-text-muted)]", children: "Domains Covered" }),
            /* @__PURE__ */ jsx("div", { className: "text-2xl font-black mt-1", children: new Set(selectedQuestions.filter((q) => q.selected).map((q) => q.domain)).size }),
            reviewAiBatchDomainLine && /* @__PURE__ */ jsxs("p", {
                    className: "text-[10px] text-[color:var(--color-text-muted)] mt-2 leading-relaxed",
                    children: [
                      /* @__PURE__ */ jsx("span", { className: "font-bold text-[color:var(--color-text-muted-dark)]", children: "AI batch domains: " }),
                      reviewAiBatchDomainLine
                    ]
                  })
                  ]
                }),
          /* @__PURE__ */ jsxs("div", {
                  className: "bg-[color:var(--color-surface-sunken)] rounded-xl p-4 border", children: [
            /* @__PURE__ */ jsx("div", { className: "text-xs font-bold text-[color:var(--color-text-muted)]", children: "Issues/Threats Selected" }),
            /* @__PURE__ */ jsx("div", { className: "text-2xl font-black mt-1", children: Object.values(selectedIssueRows).flat().length })
                  ]
                })
                ]
              }),
        /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: () => void goToContractsQuestionnaireStep(),
                  disabled: onboardingLocked,
                  className: `mt-4 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${onboardingLocked ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)]" : "bg-[color:var(--color-accent-fill)] text-[color:var(--color-ok-fg)] hover:opacity-90"}`,
                  children: onboardingLocked ? "\u2713 Questionnaire Finalized" : "Finalize Questionnaire",
                }
              )
              ]
            })
          }),
          leadSubStep === "contracts" && /* @__PURE__ */ jsxs("div", {
            className: "space-y-4",
                    children: [
              /* @__PURE__ */ jsx(GapQuestionnaireContractsPanel, {
                project,
                qModalSections,
                setQModalSections,
                isQModalEditMode,
                setIsQModalEditMode,
                questionnaireFinalized,
                questionnaireSaveBusy,
                saveQuestionnaire,
                step3Status,
                isLeadAdvisor,
                isAdvisor,
                openOnboardingReviewModal,
                riskScale,
                domains,
                selectedQuestions,
                selectedIssueRows,
              }),
            ],
          }),
        ]
      }),
      showAdvisorTrack && /* @__PURE__ */ jsxs("div", {
        className: "space-y-6", children: [
          /* @__PURE__ */ jsx("input", {
            ref: artefactFileInputRef,
            type: "file",
            accept: ".doc,.docx,.pdf,.ppt,.pptx",
            className: "hidden",
            onChange: handleArtefactTemplateFileSelected,
          }),
          /* @__PURE__ */ jsx(SubStepNav, {
            className: "mb-6",
            steps: ADVISOR_SUBSTEPS.map((s) => ({
              ...s,
              completed: isAdvisorSubStepComplete(s.key),
              attentionTitle: advisorOnboardingAttentionKey === s.key ? "Your next artefact step" : undefined,
            })),
            activeKey: advisorSubStep,
            onSelect: (key) => {
              setAdvisorSubStep(key);
              void persistActiveAdvisorSubStep(key);
            },
          }),
          onboardingLocked && /* @__PURE__ */ jsx("div", {
            className: "rounded-2xl border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-6 py-4 text-sm text-[color:var(--color-ok-fg)] font-medium",
            children:
              "Questionnaire finalized - lead-advisor onboarding is read-only. You can review artefact steps; editing is disabled.",
          }),
          advisorArtefactsLocked && /* @__PURE__ */ jsx("div", {
            className: "rounded-2xl border border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] px-6 py-4 text-sm text-[color:var(--color-accent)] font-medium",
            children:
              advisorArtefactsStatus === "approved"
                ? "Lead Advisor approved your artefact classification and templates. Editing is disabled; you can still review all tabs."
                : "Submitted to the Lead Advisor for review. Editing is disabled until they return this for revision or approve it.",
          }),
          advisorSubStep === "classification" && /* @__PURE__ */ jsxs("div", {
            className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-5 border-b border-[color:var(--color-border)] flex flex-wrap items-center justify-between gap-3", children: [
          /* @__PURE__ */ jsxs("div", {
                children: [
            /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Artefacts Index" }),
            /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Import loads every template from the artefact catalogue folder in blob storage (e.g. 07.Control implementation / Artefact templates). No database mapping - one row per file. Then classify, or upload a template manually." })
                ]
              }),
          /* @__PURE__ */ jsxs("div", {
            className: "flex flex-wrap items-center gap-2 shrink-0",
            children: [
          /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => void syncArtefactRowsFromPolicyHub(),
            disabled: advisorArtefactsLocked || onboardingLocked || syncingArtefactsFromPolicyHub,
            className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-hero)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-bg-alt)] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
            children: syncingArtefactsFromPolicyHub ? "Importing\u2026" : "Import all catalogue templates",
          }),
          /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => artefactFileInputRef.current?.click(),
            disabled: advisorArtefactsLocked || onboardingLocked || artefactUploadBusy,
            className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] disabled:cursor-not-allowed",
            children: artefactUploadBusy ? "Uploading\u2026" : "Upload template",
          }),
            ],
            }),
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "border-b border-[color:var(--color-border)] px-8 py-6 space-y-4 bg-[color:var(--color-surface-sunken)]",
              children: [
                /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Artefact index classification" }),
                /* @__PURE__ */ jsx("h5", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Document classification criteria" }),
                /* @__PURE__ */ jsx("p", {
                  className: "text-[10px] text-[color:var(--color-text-muted)] font-medium max-w-3xl",
                  children: "Create and classify artefact templates for the project. Each row below is a classification type you can select in the artefacts table.",
                }),
                /* @__PURE__ */ jsx("div", {
                  className: "overflow-x-auto",
                  children: /* @__PURE__ */ jsxs("table", {
                    className: "w-full text-left",
                    children: [
                      /* @__PURE__ */ jsx("thead", {
                        children: /* @__PURE__ */ jsxs("tr", {
                          className: "bg-[color:var(--color-accent-fill)] text-[10px] font-black uppercase tracking-[0.15em] text-white/80",
                          children: [
                            /* @__PURE__ */ jsx("th", { className: "px-6 py-4 w-[60px]", children: "#" }),
                            /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[220px]", children: "Classification type" }),
                            /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[240px]", children: "Distribution protocol" }),
                            /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[120px]", children: "Badge" }),
                          ],
                        }),
                      }),
                      /* @__PURE__ */ jsx("tbody", {
                        className: "divide-y divide-[color:var(--color-border)] bg-[color:var(--color-bg-white)]",
                        children: docClassRows.map((dc, idx) =>
                          /* @__PURE__ */ jsxs(
                            "tr",
                            {
                              className: "hover:bg-[color:var(--color-bg-alt)] transition-all",
                              children: [
                                /* @__PURE__ */ jsx("td", { className: "px-6 py-3 text-sm font-bold text-[color:var(--color-text-muted)]", children: idx + 1 }),
                                /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-sm text-[color:var(--color-text-hero)] font-medium", children: dc.classificationType || "-" }),
                                /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-xs text-[color:var(--color-text-muted-dark)]", children: dc.distributionProtocol || "-" }),
                                /* @__PURE__ */ jsx("td", {
                                  className: "px-4 py-3",
                                  children: /* @__PURE__ */ jsx("span", {
                                    className: `inline-flex px-2 py-0.5 rounded text-[8px] font-black uppercase border ${classificationColors[dc.classificationType] || "bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted-dark)] border-[color:var(--color-border)]"}`,
                                    children: classificationBadgeShortLabel[dc.classificationType] || "-",
                                  }),
                                }),
                              ],
                            },
                            dc.id,
                          ),
                        ),
                      }),
                    ],
                  }),
                }),
              ],
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "border-b border-[color:var(--color-border)] px-8 py-6 space-y-3 bg-[color:var(--color-bg-white)]",
              children: [
                /* @__PURE__ */ jsxs("div", {
                  className: "space-y-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-5 py-4",
                  children: [
                    /* @__PURE__ */ jsx("h5", {
                      className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-hero)]",
                      children: "Sample Docs",
                    }),
                    /* @__PURE__ */ jsx("p", {
                      className: "text-[10px] text-[color:var(--color-text-muted)] font-medium",
                      children: "Educator reference templates. Preview opens in a popup.",
                    }),
                    ONBOARDING_SAMPLE_ARTEFACT_ROWS.map((sampleRow) =>
                      /* @__PURE__ */ jsxs(
                        "div",
                        {
                          className: "flex flex-wrap items-center gap-4 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-4 py-3",
                          children: [
                            /* @__PURE__ */ jsxs("div", {
                              className: "min-w-0 flex-1 space-y-0.5",
                              children: [
                                /* @__PURE__ */ jsx("p", {
                                  className: "text-[10px] font-medium text-[color:var(--color-text-muted)] break-all",
                                  children: stripTrailingDocumentExtension(sampleRow.fileNameWithExt),
                                }),
                                /* @__PURE__ */ jsx("p", {
                                  className: "text-sm font-bold text-[color:var(--color-text-hero)] line-clamp-2",
                                  children: sampleRow.title,
                                }),
                              ],
                            }),
                            /* @__PURE__ */ jsx("span", {
                              className:
                                "inline-flex shrink-0 px-2 py-0.5 rounded text-[8px] font-black uppercase border bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]",
                              children: "Public",
                            }),
                            /* @__PURE__ */ jsx("button", {
                              type: "button",
                              onClick: () => setAdvisorOnboardingSamplePreview(sampleRow),
                              className:
                                "shrink-0 px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg",
                              children: "Preview",
                            }),
                          ],
                        },
                        sampleRow.id,
                      ),
                    ),
                  ],
                }),
              ],
            }),
        /* @__PURE__ */ jsx("div", {
              className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", {
                className: "w-full text-left", children: [
          /* @__PURE__ */ jsx("thead", {
                  children: /* @__PURE__ */ jsxs("tr", {
                    className: "bg-[color:var(--color-accent-fill)] text-[10px] font-black uppercase tracking-[0.15em] text-white/80", children: [
            /* @__PURE__ */ jsx("th", { className: "px-6 py-4 w-[60px]", children: "#" }),
            /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[250px]", children: "Artefact Name" }),
            /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[150px]", children: "Template file" }),
            /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[280px]", children: "Classification" }),
            /* @__PURE__ */ jsx("th", { className: "px-4 py-4 w-[80px]", children: "Actions" })
                    ]
                  })
                }),
          /* @__PURE__ */ jsxs("tbody", {
                  className: "divide-y divide-[color:var(--color-border)]", children: [
                    artefactIndex.length === 0 && /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { colSpan: 5, className: "px-6 py-10 text-center text-sm text-[color:var(--color-text-muted)]", children: "No artefacts yet. Click Import all catalogue templates (loads every file from the artefact templates blob folder), or use Upload template." }) }),
                    artefactIndex.map((a, i) => /* @__PURE__ */ jsxs("tr", {
                      className: "group hover:bg-[color:var(--color-bg-alt)] transition-all", children: [
              /* @__PURE__ */ jsx("td", { className: "px-6 py-3 text-sm font-bold text-[color:var(--color-text-muted)]", children: i + 1 }),
              /* @__PURE__ */ jsx("td", {
                        className: "px-4 py-3", children: /* @__PURE__ */ jsxs("div", {
                          className: "flex items-center gap-2",
                          children: [
                            /* @__PURE__ */ jsx(
                              "input",
                              {
                                value: a.name,
                                onChange: (e) => updateArtefactRow(a.id, "name", e.target.value),
                                disabled: advisorArtefactsLocked || onboardingLocked,
                                placeholder: "Enter artefact name...",
                                className: "w-full bg-transparent border-b border-transparent hover:border-[color:var(--color-border)] focus:border-[color:var(--color-accent)] outline-none text-sm text-[color:var(--color-text-hero)] py-1 transition-colors disabled:opacity-60"
                              }
                            ),
                            /* @__PURE__ */ jsx(Tooltip, {
                              title: "AI-suggest a name - you review and apply, nothing is written automatically",
                              children: /* @__PURE__ */ jsx("button", {
                                type: "button",
                                onClick: () => requestAiNameSuggestions(a),
                                disabled: advisorArtefactsLocked || onboardingLocked,
                                className: "shrink-0 px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border border-[color:var(--color-info-line)] text-[color:var(--color-accent)] hover:bg-[color:var(--color-accent-tint)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors",
                                children: "AI Suggest",
                              }),
                            }),
                          ],
                        })
                      }),
              /* @__PURE__ */ jsx("td", {
                        className: "px-4 py-3",
                        children: hasImportedArtefactTemplate(a)
                          ? /* @__PURE__ */ jsx("span", {
                              className: "text-sm text-[color:var(--color-text-hero)]",
                              children: artefactFileNameUiDisplay(a) || "-",
                            })
                          : /* @__PURE__ */ jsx(
                              "input",
                              {
                                value: a.templateId,
                                onChange: (e) => updateArtefactRow(a.id, "templateId", e.target.value),
                                disabled: advisorArtefactsLocked || onboardingLocked,
                                placeholder: "e.g. ISMS-DOC-01",
                                className: "w-full bg-transparent border-b border-transparent hover:border-[color:var(--color-border)] focus:border-[color:var(--color-accent)] outline-none text-sm text-[color:var(--color-text-hero)] py-1 transition-colors disabled:opacity-60",
                              },
                            ),
                      }),
              /* @__PURE__ */ jsx("td", {
                        className: "px-4 py-3", children: /* @__PURE__ */ jsx(
                          "select",
                          {
                            value: a.classification,
                            onChange: (e) => updateArtefactRow(a.id, "classification", e.target.value),
                            disabled: advisorArtefactsLocked || onboardingLocked,
                            className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-1.5 text-xs font-bold text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] disabled:opacity-60",
                            children: docClassRows.map((d) => /* @__PURE__ */ jsx("option", { value: d.classificationType, children: d.classificationType }, d.id))
                          }
                        )
                      }),
              /* @__PURE__ */ jsx("td", {
                className: "px-4 py-3",
                children: /* @__PURE__ */ jsx("button", {
                  type: "button",
                  disabled: advisorArtefactsLocked || onboardingLocked,
                  onClick: () => deleteArtefactRow(a.id),
                  className: "p-1.5 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger-fg)] transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-30",
                  title: "Delete",
                  children: /* @__PURE__ */ jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" }) }),
                }),
              })
                      ]
                    }, a.id))
                  ]
                })
                ]
              })
            }),
        !advisorArtefactsLocked && !onboardingLocked && /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-4 border-t border-[color:var(--color-border)] flex flex-wrap justify-end gap-2",
              children: [
                /* @__PURE__ */ jsx("button", {
                  type: "button",
                  onClick: () => void saveAdvisorArtefactClassification(),
                  disabled: advisorArtefactsSaveBusy,
                  className: "px-5 py-2.5 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-hero)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-bg-alt)] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
                  children: advisorArtefactsSaveBusy ? "Saving\u2026" : "Save",
                }),
                artefactIndex.length > 0 &&
                  /* @__PURE__ */ jsx("button", {
                    type: "button",
                    onClick: () => void submitAdvisorArtefactsForLdReview(),
                    disabled: advisorArtefactsSaveBusy || onboardingLocked,
                    className: "px-5 py-2.5 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] disabled:cursor-not-allowed",
                    children: "Submit for LD review",
                  }),
              ],
            }),
            /* @__PURE__ */ jsx(OnboardingSampleDocxPreviewModal, {
              open: advisorOnboardingSamplePreview != null,
              sample: advisorOnboardingSamplePreview,
              onClose: () => setAdvisorOnboardingSamplePreview(null),
              getApiToken,
            }),
            ]
          }),
          advisorSubStep === "contracts" && /* @__PURE__ */ jsxs("div", {
            className: "space-y-4",
            children: [
              questionnaireFinalized
                ? /* @__PURE__ */ jsx(GapQuestionnaireContractsPanel, {
                    project,
                    qModalSections,
                    setQModalSections,
                    isQModalEditMode: false,
                    setIsQModalEditMode: () => {},
                    questionnaireFinalized: true,
                    questionnaireSaveBusy: false,
                    saveQuestionnaire: () => Promise.resolve(),
                    step3Status,
                    isLeadAdvisor: false,
                    isAdvisor: true,
                    openOnboardingReviewModal,
                    riskScale,
                    domains,
                    selectedQuestions,
                    selectedIssueRows,
                  })
                : /* @__PURE__ */ jsx("div", {
                    className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl p-8 text-sm text-[color:var(--color-text-muted-dark)] font-medium",
                    children:
                      "Questionnaire has not been finalized by the Lead Advisor yet. Once submitted for review, it will appear here.",
                  }),
            ],
          }),
          /* @__PURE__ */ jsx(Modal, {
            title: "New artefact from upload",
            open: artefactUploadModalOpen,
            onCancel: closeArtefactUploadModal,
            footer: /* @__PURE__ */ jsxs("div", {
              className: "flex flex-wrap justify-end gap-2",
              children: [
                /* @__PURE__ */ jsx("button", {
                  type: "button",
                  onClick: closeArtefactUploadModal,
                  className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-bg-alt)] transition-all",
                  children: "Cancel",
                }),
                /* @__PURE__ */ jsx("button", {
                  type: "button",
                  onClick: () => void confirmArtefactUploadModal(),
                  disabled: artefactUploadBusy,
                  className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-sm disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] disabled:cursor-not-allowed",
                  children: "Add artefact",
                }),
              ],
            }),
            children: /* @__PURE__ */ jsxs("div", {
              className: "space-y-4 pt-1",
              children: [
                /* @__PURE__ */ jsxs("div", {
                  children: [
                    /* @__PURE__ */ jsx("label", {
                      className: "text-xs font-bold text-[color:var(--color-text-muted)] uppercase tracking-wide block mb-1",
                      children: "Filename",
                    }),
                    /* @__PURE__ */ jsx("p", {
                      className: "text-sm text-[color:var(--color-text-hero)] break-all bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-2",
                      children: pendingArtefactFileName || "-",
                    }),
                  ],
                }),
                /* @__PURE__ */ jsxs("div", {
                  children: [
                    /* @__PURE__ */ jsx("label", {
                      className: "text-xs font-bold text-[color:var(--color-text-muted)] uppercase tracking-wide block mb-1",
                      children: "Title",
                    }),
                    /* @__PURE__ */ jsx(Input, {
                      value: artefactUploadTitle,
                      onChange: (e) => setArtefactUploadTitle(e.target.value),
                      placeholder: "Artefact title",
                      className: "w-full",
                    }),
                  ],
                }),
                /* @__PURE__ */ jsxs("div", {
                  children: [
                    /* @__PURE__ */ jsx("label", {
                      className: "text-xs font-bold text-[color:var(--color-text-muted)] uppercase tracking-wide block mb-1",
                      children: "Visibility",
                    }),
                    /* @__PURE__ */ jsx(
                      "select",
                      {
                        value: artefactUploadClassification,
                        onChange: (e) => setArtefactUploadClassification(e.target.value),
                        className:
                          "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-sm font-bold text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]",
                        children: docClassRows.map((d) =>
                          /* @__PURE__ */ jsx("option", { value: d.classificationType, children: d.classificationType }, d.id),
                        ),
                      },
                    ),
                  ],
                }),
              ],
            }),
          }),
          aiNamingModal && /* @__PURE__ */ jsx(Modal, {
            open: true,
            title: "AI-drafted document name suggestions",
            onCancel: closeAiNamingModal,
            width: 640,
            footer: /* @__PURE__ */ jsxs("div", {
              className: "flex justify-end gap-2",
              children: [
                /* @__PURE__ */ jsx("button", {
                  type: "button",
                  onClick: closeAiNamingModal,
                  className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-bg-alt)] transition-all",
                  children: "Cancel",
                }),
                /* @__PURE__ */ jsx("button", {
                  type: "button",
                  onClick: applyAiNameSuggestion,
                  disabled: !aiNamingModal.selectedName,
                  className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${aiNamingModal.selectedName ? "bg-[color:var(--color-accent-fill)] text-white hover:opacity-90" : "bg-[color:var(--color-bg-alt)] text-white/80 cursor-not-allowed"}`,
                  children: "Apply selected name",
                }),
              ],
            }),
            children: /* @__PURE__ */ jsxs("div", {
              className: "space-y-4 pt-1",
              children: [
                /* @__PURE__ */ jsx(Tag, { color: "purple", children: "AI drafted - review before use" }),
                aiNamingModal.loading && /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center gap-2 text-sm text-[color:var(--color-text-muted)] py-8 justify-center",
                  children: [ /* @__PURE__ */ jsx(Spin, { size: "small" }), "Generating suggestions…" ],
                }),
                aiNamingModal.error && !aiNamingModal.loading && /* @__PURE__ */ jsx(Alert, {
                  type: "error",
                  showIcon: true,
                  message: aiNamingModal.error,
                }),
                !aiNamingModal.loading && !aiNamingModal.error && /* @__PURE__ */ jsxs(Fragment, {
                  children: [
                    /* @__PURE__ */ jsxs("p", {
                      className: "text-[11px] text-[color:var(--color-text-muted)]",
                      children: [
                        "Convention: ",
                        /* @__PURE__ */ jsx("code", { className: "bg-[color:var(--color-surface-sunken)] px-1.5 py-0.5 rounded", children: aiNamingModal.conventionPattern || "-" }),
                        " (",
                        aiNamingModal.conventionPatternSource === "project"
                          ? "project's saved convention"
                          : aiNamingModal.conventionPatternSource === "body"
                            ? "convention entered"
                            : "platform default",
                        ")",
                      ],
                    }),
                    /* @__PURE__ */ jsx(Alert, {
                      type: "warning",
                      showIcon: true,
                      message:
                        "Run-agreement score not returned for this output - review every suggestion manually before applying.",
                    }),
                    aiNamingModal.warnings.length > 0 && /* @__PURE__ */ jsx(Alert, {
                      type: "info",
                      showIcon: true,
                      message: `Normaliser warnings: ${aiNamingModal.warnings.join(", ")}`,
                    }),
                    aiNamingModal.suggestions.length === 0
                      ? /* @__PURE__ */ jsx(Empty, { description: "No suggestions returned." })
                      : /* @__PURE__ */ jsx(Radio.Group, {
                          className: "w-full",
                          value: aiNamingModal.selectedName,
                          onChange: (e) =>
                            setAiNamingModal((prev) => (prev ? { ...prev, selectedName: e.target.value } : prev)),
                          children: /* @__PURE__ */ jsx(Space, {
                            direction: "vertical",
                            className: "w-full",
                            children: aiNamingModal.suggestions.map((s, i) =>
                              /* @__PURE__ */ jsx(
                                Radio,
                                {
                                  value: s.name,
                                  className: "w-full !flex items-start py-2 border-b border-[color:var(--color-border)] last:border-0",
                                  children: /* @__PURE__ */ jsxs("div", {
                                    children: [
                                      /* @__PURE__ */ jsxs("div", {
                                        className: "flex items-center gap-2 flex-wrap",
                                        children: [
                                          /* @__PURE__ */ jsx("span", { className: "font-bold text-sm text-[color:var(--color-text-hero)]", children: s.name }),
                                          /* @__PURE__ */ jsx(Tag, {
                                            color: s.convention_match ? "green" : "orange",
                                            children: s.convention_match ? "Matches convention" : "Convention variant",
                                          }),
                                        ],
                                      }),
                                      s.explanation && /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted)] mt-0.5", children: s.explanation }),
                                    ],
                                  }),
                                },
                                `${s.name}-${i}`,
                              ),
                            ),
                          }),
                        }),
                    aiNamingModal.meta?.requestId && /* @__PURE__ */ jsxs("p", {
                      className: "text-[10px] text-[color:var(--color-text-muted)]",
                      children: [ "Request ID: ", aiNamingModal.meta.requestId ],
                    }),
                  ],
                }),
              ],
            }),
          }),
        ]
      }),
      isSPOC && /* @__PURE__ */ jsxs("div", {
        className: "space-y-4", children: [
      /* @__PURE__ */ jsxs("div", {
        className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl p-8 space-y-4", children: [
      /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Project Onboarding Summary" }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted)]", children: "As a Company SPOC, you can review and approve the onboarding deliverables once submitted." }),
      /* @__PURE__ */ jsxs("div", {
          className: "grid grid-cols-2 gap-4", children: [
        /* @__PURE__ */ jsxs("div", {
            className: "bg-[color:var(--color-surface-sunken)] rounded-xl p-4 border", children: [
          /* @__PURE__ */ jsx("div", { className: "text-xs font-bold text-[color:var(--color-text-muted)]", children: "Status" }),
          /* @__PURE__ */ jsx("div", { className: "text-lg font-black mt-1", children: step3StatusForUi })
            ]
          }),
        /* @__PURE__ */ jsxs("div", {
            className: "bg-[color:var(--color-surface-sunken)] rounded-xl p-4 border", children: [
          /* @__PURE__ */ jsx("div", { className: "text-xs font-bold text-[color:var(--color-text-muted)]", children: "Framework" }),
          /* @__PURE__ */ jsx("div", { className: "text-lg font-black mt-1", children: standardsLabel(project) || "-" })
            ]
          })
          ]
        })
        ]
      }),
      questionnaireFinalized
        ? /* @__PURE__ */ jsx(GapQuestionnaireContractsPanel, {
            project,
            qModalSections,
            setQModalSections,
            isQModalEditMode: false,
            setIsQModalEditMode: () => {},
            questionnaireFinalized: true,
            questionnaireSaveBusy: false,
            saveQuestionnaire: () => Promise.resolve(),
            step3Status,
            isLeadAdvisor: false,
            isAdvisor: false,
            openOnboardingReviewModal: () => {},
            riskScale,
            domains,
            selectedQuestions,
            selectedIssueRows,
          })
        : /* @__PURE__ */ jsx("div", {
            className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl p-8 text-sm text-[color:var(--color-text-muted-dark)] font-medium",
            children: "Questionnaire has not been finalized by the Lead Advisor yet. Once submitted for review, it will appear here.",
          }),
        ]
      }),
      isOnboardingReviewOpen && /* @__PURE__ */ jsx("div", {
        className: "fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md p-4",
        children: /* @__PURE__ */ jsxs("div", {
          className: "bg-[color:var(--color-bg-white)] rounded-[2rem] shadow-2xl border border-[color:var(--color-border)] w-full max-w-md overflow-hidden",
          children: [
            /* @__PURE__ */ jsx("div", {
              className: "px-8 py-5 border-b border-[color:var(--color-border)] bg-[color:var(--color-accent-fill)] text-white rounded-t-[2rem]",
              children: /* @__PURE__ */ jsx("h3", {
                className: "text-sm font-black uppercase tracking-widest",
                children: onboardingReviewTarget === "Under Review" ? "Send for Review" : onboardingReviewTarget === "Draft" ? "Send for Re-review" : onboardingReviewTarget === "Approved by SPOC" ? "Approve Onboarding" : "Update Status",
              }),
            }),
            /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-4",
              children: [
                /* @__PURE__ */ jsx("label", { className: "text-xs font-bold text-[color:var(--color-text-muted-dark)] uppercase tracking-wide", children: "Add a comment (optional)" }),
                /* @__PURE__ */ jsx("textarea", {
                  value: onboardingReviewComment,
                  onChange: (e) => setOnboardingReviewComment(e.target.value),
                  className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl p-4 text-sm text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] focus:bg-[color:var(--color-bg-white)] transition-all min-h-[100px]",
                  placeholder: "Add notes for the reviewer...",
                }),
                /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center justify-end gap-2 mt-4",
                  children: [
                    /* @__PURE__ */ jsx("button", { type: "button", onClick: () => setIsOnboardingReviewOpen(false), disabled: onboardingReviewSubmitting, className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-bg-alt)] transition-all disabled:opacity-50", children: "Cancel" }),
                    /* @__PURE__ */ jsx("button", { type: "button", onClick: () => submitOnboardingReviewWithComment(), disabled: onboardingReviewSubmitting, className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-sm disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]", children: onboardingReviewSubmitting ? "Submitting..." : "Submit" }),
                  ],
                }),
              ],
            }),
          ],
        }),
      }),
    ]
  });
};
export default ProjectOnboarding;
